import { dataService } from '@app/modules/data';
import { DataObject, ObjectOptions } from './base';

import { AppConstants } from '@app/app.constants';

import { Session } from './session';
import { User } from './user';
import { Place } from './place';
import { Audit } from './audit';
import { QrCode } from './qrcode';
import { AskWaiter } from './askwaiter';
import { Product } from './product';
import { Offer } from './offer';
import { Extra } from './extra';
import { TicketProduct } from './ticketproduct';
import { ProductOption } from './productoption';
import { TicketOffer } from './ticketoffer';
import { TicketExtra } from './ticketextra';
import { TicketInvoice } from './ticketinvoice';
import { TicketDiscount } from './ticketdiscount';
import { TicketChange } from './ticketchange';
import { TicketMixedPayment } from './ticketmixed';
import { TicketBAI } from './ticketbai';
import { TicketSII } from './ticketsii';
import { TicketAudit } from './ticketaudit';
import { Payment } from './payment';
import { PayAccount } from './payaccount';
import { AccountInvoice } from './accountinvoice';

import { Subject } from 'rxjs';

/*******************************/
/* PUBLIC TYPES                */
/*******************************/

export type TaxBreakdown = Array<{ rate: number, base: number, taxes: number, total: number }>;

/**
 * Token de exclusion para emits que se reparten entre suscriptores que
 * NO deben ejecutar la misma accion mas de una vez (p.ej. el editor y
 * el thumb de tab-tickets, ambos suscritos al OnPrint/OnCommit del
 * mismo Ticket). El primer suscriptor que llama take() obtiene true;
 * los demas reciben false y se saltan la accion. Cada emit crea un
 * claim nuevo, asi que no hay estado entre commits.
 */
export interface PrintClaim {
    take(): boolean;
}

function _makePrintClaim(): PrintClaim {
    let _taken = false;
    return {
        take(): boolean {
            if (_taken) return false;
            _taken = true;
            return true;
        }
    };
}

/*******************************/
/* GLOBAL MUTEX                */
/*******************************/

import { GlobalMutex } from '@app/app.commonutils';
import { VeriFactu } from './verifactu';
const _globalmutex = new GlobalMutex();

/*******************************/
/* TICKET INSTANCE             */
/*******************************/

export interface _Ticket {
    status: string;
    place: number;
    session: number;
    waiter: number;     // USER objid del camarero que editó por último el ticket
    paidby: number;
    paidon: Date;
    paidin: number;
    qrcode: number;
    price: number;
    refund: number;
    taxes: number;
    orderno: string;
    series: string;
    invceno: string;
    elapsed: number;
    comments: string;
    payment: string;
    transaction: number;
    account: number;
    invceac: number;
    given: number;
    flags: string;
    tmcommit: Date;
    duedays: number;
    diners: number;

    client?: number;
    sentto?: string;
    intill?: number;
    pmixed?: string;
    device?: string;
};

interface _TicketData extends _Ticket {
    objid?: number;
    _uuid?: string;
    created?: Date;
    updated?: Date;
};

abstract class TicketData extends DataObject {
    protected _ticket: _TicketData = {
        status: null,
        place: null,
        session: null,
        waiter: null,
        paidby: null,
        paidon: null,
        paidin: null,
        qrcode: null,
        price: null,
        refund: null,
        taxes: null,
        orderno: null,
        series: null,
        invceno: null,
        elapsed: null,
        comments: null,
        payment: null,
        transaction: null,
        account: null,
        invceac: null,
        given: null,
        flags: null,        // see TicketFlags definition below
        tmcommit: null,
        duedays: null,
        diners: null,

        client: null,
        sentto: null,
        intill: null,
        pmixed: null,
        device: null
    };

    constructor(table: string, objid: string, data: dataService, objoptions: ObjectOptions){
        super(table, objid, data, objoptions);
        this._ticket.created = new Date();
    }
   
    /****************************/
    /* TICKET FLAGS             */
    /****************************/

    protected TicketFlags = Object.freeze({
        NewTicketPrinted: 0,        // the 'new ticket' has been sent to printer
        TicketReadyNotified: 1,     // the 'ticket ready' has been notified to client
        TicketReady: 2,             // the ticket is ready
        TicketToPay: 3,             // ticket payment has been requested
        TicketPaid: 4               // ticket has been paid
    });

    protected abstract _UpdateFlags();
    protected get flags(): Array<any> {
        let _array = [];

        // backwards compatibility for old tickets
        if (this._isLoaded && !this._ticket.flags){
            this._UpdateFlags();     
        }

        if (!this._ticket.flags){
            return [];  // not loaded
        }

        let _flags = Array.from(this._ticket.flags);
        for(let i=0; i < _flags.length; i++){
            _array[i] = (_flags[i] == '1');
        }

        return _array;
    }

    protected set flags(value: Array<any>) {
        let _array = [];
        for(let i=0; i < value.length; i++){
            _array[i] = (value[i] === true) ? '1' : '0';
        }

        let _flags = _array.join(''); 
        if(this.patchValue(this._ticket, 'flags', _flags)){
            this.ToUpdate = true;
        }
    }

    protected _GetFlagValue(flag){
        return !!this.flags[flag];
    }

    protected _SetFlagValue(flag, value){
        let _flags = this.flags;
        _flags[flag] = value;
        this.flags = _flags;
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get created(): Date{
        return this._ticket.created;
    }

    set created(value: Date){
        if(this.patchValue(this._ticket, 'created', value ? value : new Date())){
            this.ToUpdate = true;
        }
    }

    get updated(): Date {
        return this._ticket.updated || this._ticket.created;
    }

    get printed(): boolean {
        return this._GetFlagValue(this.TicketFlags.NewTicketPrinted);
    }

    set printed(value: boolean){
        this._SetFlagValue(this.TicketFlags.NewTicketPrinted, value);
    }

    get ready(): boolean {
        return this._GetFlagValue(this.TicketFlags.TicketReadyNotified);
    }

    set ready(value: boolean) {
        this._SetFlagValue(this.TicketFlags.TicketReadyNotified, value);
    }

    get status(): string{
        return this._ticket.status;
    }

    set status(value: string){
        if(this.patchValue(this._ticket, 'status', value)){
            this.ToUpdate = true;
        }
    }

    get place(): Place {
        return this._children['place'] || null;
    }

    set place(value: Place){
        if (this.SetChild('place', value, 'place')){
            this.ToUpdate = true;
        }
    }

    get session(): Session {
        return this._children['session'] || null;
    }

    set session(value: Session){
        if (this.SetChild('session', value, 'session')){
            this.ToUpdate= true;
        }
    }

    // Camarero (USER) atribuido al ticket = el último que lo editó (se reescribe
    // en cada edición humana, ver _StampWaiter). Referencia a USER: el objid se
    // envía como CAMPO (this._ticket.waiter), NO por Depend, para que el server no
    // notifique la fila USER (si no, su PII — email/teléfono — se propagaría a
    // invitados/delivery). El hijo User solo se mantiene para mostrar el nombre.
    get waiter(): User {
        return this._children['waiter'] || null;
    }

    set waiter(value: User){
        if (this.SetChild('waiter', value, 'waiter')){
            // mantener el objid de backing (lo envía Change; ya no hay Depend)
            this.patchValue(this._ticket, 'waiter', value ? parseInt(value.objid) : null);
            this.ToUpdate = true;
        }
    }

    // objid de USER del camarero del ticket, desde el campo CRUDO de backing (no el hijo
    // User, que solo se carga para mostrar el nombre y puede ser null). Fiable para atribuir
    // impresión/cajón al mismo camarero que el ticket (igual que TICKET.waiter del e0).
    get WaiterId(): number {
        return this._ticket.waiter ? Number(this._ticket.waiter) : null;
    }

    get paidby(): Session {
        return this._children['paidby'] || null;
    }

    set paidby(value: Session) {
        if (this.SetChild('paidby', value, 'paidby')){
            this.ToUpdate= true;
        }
    }

    get paidon(): Date{
        return this._ticket.paidon;
    }

    set paidon(value: Date){
        if(this.patchValue(this._ticket, 'paidon', value)){
            this.ToUpdate = true;
        }
    }

    get paidin(): Session {
        return this._children['paidin'] || null;
    }

    set paidin(value: Session) {
        if (this.SetChild('paidin', value, 'paidin')){
            this.ToUpdate= true;
        }
    }

    get qrcode() : QrCode {
        return this._children['qrcode'] || null;
    }

    set qrcode(value: QrCode){
        if (this.SetChild('qrcode', value, 'qrcode')){
            this.ToUpdate = true;
        }
    }

    get price(): number{
        return this._ticket.price;
    }

    set price(value: number){
        if(this.patchValue(this._ticket, 'price', value)){
            this.ToUpdate = true;
        }
    }

    get refund(): number{
        return this._ticket.refund;
    }

    set refund(value: number){
        if(this.patchValue(this._ticket, 'refund', value)){
            this.ToUpdate = true;
        }
    }

    get taxes(): number {
        if (this._ticket.taxes === null){   // backwards compatibility: no taxes information
            return this._ticket.price - (this._ticket.price / ((100 + Number(this.place.TaxRate))/100));
        }

        return this._ticket.taxes;
    }

    set taxes(value: number){
        if(this.patchValue(this._ticket, 'taxes', value)){
            this.ToUpdate = true;
        }
    }

    get orderno(): string{
        return this._ticket.orderno;
    }

    set orderno(value: string){
        if(this.patchValue(this._ticket, 'orderno', value)){
            this.ToUpdate = true;
        }
    }

    get elapsed(): number{
        return this._ticket.elapsed;
    }

    set elapsed(value: number){
        if(this.patchValue(this._ticket, 'elapsed', value)){
            this.ToUpdate = true;
        }
    }

    get comments(): string{
        return this._ticket.comments;
    }

    set comments(value: string){
        if(this.patchValue(this._ticket, 'comments', value)){
            this.ToUpdate = true;
        }
    }

    get payment(): string{
        return this._ticket.payment;
    }

    set payment(value: string){
        if(this.patchValue(this._ticket, 'payment', value)){
            this.ToUpdate = true;
        }
    }

    get transaction(): Payment {
        return this._children['transaction'] || null;
    }

    get tmcommit(): Date{
        return this._ticket.tmcommit;
    }

    set tmcommit(value: Date){
        if(this.patchValue(this._ticket, 'tmcommit', value)){
            this.ToUpdate = true;
        }
    }

    get account(): PayAccount {
        return this._children['account'] || null;
    }

    set account(value: PayAccount) {
        if (this.SetChild('account', value, 'account')){
            this.ToUpdate= true;
        }
    }

    get invceac(): AccountInvoice {
        return this._children['invceac'] || null;
    }

    set invceac(value: AccountInvoice) {
        if (this.SetChild('invceac', value, 'invceac')){
            this.ToUpdate= true;
        }
    }

    get given(): number{
        return this._ticket.given;
    }

    set given(value: number){
        if(this.patchValue(this._ticket, 'given', value)){
            this.ToUpdate = true;
        }
    }

    get duedays(): number {
        return this._ticket.duedays;
    }

    set duedays(value: number){
        if(this.patchValue(this._ticket, 'duedays', value)){
            this.ToUpdate = true;
        }
    }

    get diners(): number {
        return this._ticket.diners;
    }

    set diners(value: number){
        if(this.patchValue(this._ticket, 'diners', value)){
            this.ToUpdate = true;
        }
    }

    protected get audits(): Array <TicketAudit> {
        return this._chldlist['audit'] || [];
    }

    protected get intill(): Audit {
        return this._children['intill'] || null;
    }

    /* read only properties */

    get series(): string {
        return this._ticket.series;
    }

    get invceno(): string {
        return this._ticket.invceno;
    }

    get client(): number {
        return this._ticket.client;
    }

    // El destinatario de factura (INVOICEBUSINESS objid) lo COMPUTA ticketlst.php
    // (_ivclient/_acclient) para los informes; no es una columna del sync normal.
    // Setter para que los loaders de informe puedan aplicarlo tambien a tickets
    // ya cargados que se reutilizan (mismo patron que hasdiscount).
    set client(value: number){
        this._ticket.client = value;
    }

    get sentto(): Array<string> {
        let _sentto = [];

        if (this._ticket.sentto){
            let _srvlst = [ 'TICKETSII', 'TICKETBAI', 'VERIFACTU' ];

            let _server = Array.from(this._ticket.sentto);
            for(let i=0; i < _server.length; i++){
                if (_server[i] == '1'){
                    _sentto.push(_srvlst[i]);
                }
            }    
        }
        else {  // this ticket is active
            let _changes = this.changes.sort((a, b) => {
                return b.created.getTime() - a.created.getTime();
            });

            let _lastchange = _changes[0];
            if (_lastchange){   // sin cambios no hay servicios enviados (evita reading 'ticketsii' de undefined al descargar el informe)
                if (_lastchange.ticketsii?.IsValid){
                    _sentto.push('TICKETSII');
                }

                if (_lastchange.ticketbai?.IsValid){
                    _sentto.push('TICKETBAI');
                }

                if (_lastchange.verifactu?.IsValid){
                    _sentto.push('VERIFACTU');
                }
            }
        }

        return _sentto;
    }

    get device(){
        if (!this._ticket.device){
            return this.session.deviceid;
        }

        return this._ticket.device;
    }

    // Resolve the POS (till) that owns this ticket, mirroring the server-side
    // audit till filter (auditinfo.php) and InAudit: the till where it was
    // charged (paidin) first, then the table (qrcode) owner, then the
    // creation device. Returns null in single-POS or when unresolved.
    // Typed `any` (duck-typing, like InAudit) to avoid a ticket->svcplace
    // import cycle in the AoT build.
    get till(): any {
        let _place = this.place;
        if (!_place || !_place.MultiplePOS){
            return null;
        }

        let _services = _place.services || [];

        // 1. where the money entered (final payment session device)
        if (this.paidin){
            for (let _svc of _services){
                if (_svc.IsValid && _svc.PaidIn(this)){
                    return _svc;
                }
            }
        }

        // 2. the table (qrcode) owner — QrCode exposes its till directly, so
        // resolve it in O(1) instead of scanning every service's tables.
        let _qrcode = this.qrcode;
        if (_qrcode && _qrcode.till && _qrcode.till.IsValid){
            return _qrcode.till;
        }

        // 3. the creation device — use the ticket's PERSISTED device only; do
        // NOT fall back to the current session's device, which could attribute
        // a ticket without a stored device to whoever is looking at it.
        let _device = this._ticket.device;
        if (_device){
            for (let _svc of _services){
                if (_svc.IsValid && _svc.connects.some(_c => _c.IsValid && (_c.device == _device))){
                    return _svc;
                }
            }
        }

        return null;
    }

    get tillName(): string {
        return this.till?.name || '';
    }

    /****************************/
    /* CHILDREN MANAGEMENT      */
    /****************************/

    AddInvoice(child: TicketInvoice){
        this.AddChild('invoice', child, 'ticket');
    }

    AddProduct(child: TicketProduct){
        this.AddChild('products', child, 'ticket');
    }

    DelProduct(child: TicketProduct){
        this.DelChild('products', child, 'ticket');
    }

    AddOffer(child: TicketOffer){
        this.AddChild('offers', child, 'ticket');
    }

    DelOffer(child: TicketOffer){
        this.DelChild('offers', child, 'ticket');
    }

    AddExtra(child: TicketExtra){
        this.AddChild('extras', child, 'ticket');
    }

    DelExtra(child: TicketExtra){
        this.DelChild('extras', child, 'ticket');
    }

    AddDiscount(child: TicketDiscount){
        this.AddChild('discount', child, 'ticket');
    }

    DelDiscount(child: TicketDiscount){
        this.DelChild('discount', child, 'ticket');
    } 

    AddChange(child: TicketChange){
        this.AddChild('changes', child, 'ticket');
    }

    DelChange(child: TicketChange){
        this.DelChild('changes', child, 'ticket');
    }

    AddMixed(child: TicketMixedPayment){
        this.AddChild('mixed', child, 'ticket');
    }

    DelMixed(child: TicketMixedPayment){
        this.DelChild('mixed', child, 'ticket');
    }

    /****************************/
    /* CHILD ACCESS             */
    /****************************/

    get products() : Array <TicketProduct> {
        return this._chldlist['products'] || [];
    }

    get offers() : Array <TicketOffer> {
        return this._chldlist['offers'] || [];
    }

    get extras() : Array <TicketExtra> {
        return this._chldlist['extras'] || [];
    }

    get invoices(): Array<TicketInvoice> {
        return this._chldlist['invoice'] || [];
    }

    get discounts(): Array <TicketDiscount> {
        return this._chldlist['discount'] || [];
    }

    get changes(): Array<TicketChange> {
        return this._chldlist['changes'] || [];
    }

    get mixed(): Array<TicketMixedPayment> {
        return this._chldlist['mixed'] || [];
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    abstract get requests() : Array<AskWaiter>;
    abstract get request() : AskWaiter;
    abstract set request(value: AskWaiter);

    // Marca transitoria (NO persistida) de reapertura DELIBERADA. La pone
    // _OpenTicket (ui-view) justo antes de commitear el PD->AC. El server
    // (prev_complete_ticket_specific) usa esta marca para distinguir una
    // reapertura real de un "stale sync" de un movil dormido: sin ella, su
    // proteccion bloqueaba TODA reapertura salvo serie 'R' (que un reabrir
    // reason 'X' no genera), devolviendo el ticket a PD. One-shot: se limpia
    // al construir el Change para no marcar commits posteriores.
    public _reopenIntent: boolean = false;

    // Ventana (timestamp epoch ms) del "double-check" de reabrir. La pone
    // _OpenTicket = Date.now() + REOPEN_GUARD_MS. Mientras esta activa, un apply
    // de datos STALE (un refresh/cache pre-reabrir que aun ve el ticket en PD
    // porque el PHP todavia no ha persistido el AC) NO puede revertir el AC
    // local a PD ("se paga solo"). El PD del server es obsoleto: aun no ha visto
    // el reabrir. Expira sola -> un pago legitimo posterior si entra.
    public _reopenGuardUntil: number = 0;

    protected get Change() {
        let _change = {
            created: this._ticket.created ? this.dateStrToMysql(this._ticket.created) : null,
            series: this._ticket.series,
            invceno: this._ticket.invceno,
            place: this._ticket.place,
            status: this._ticket.status,
            session: this._ticket.session,
            waiter: this._ticket.waiter,
            paidby: this._ticket.paidby,
            paidon: this._ticket.paidon ? this.dateStrToMysql(this._ticket.paidon) : null,
            paidin: this._ticket.paidin,
            qrcode: this._ticket.qrcode || 0,
            price: this._ticket.price,
            refund: this._ticket.refund || 0,
            taxes: this._ticket.taxes,
            elapsed: this._ticket.elapsed,
            comments: this._ticket.comments,
            payment: this._ticket.payment,
            transaction: this._ticket.transaction,
            account: this._ticket.account,
            invceac: this._ticket.invceac,
            given: this._ticket.given,
            diners: this._ticket.diners,
            tmcommit: this._ticket.tmcommit ? this.dateStrToMysql(this._ticket.tmcommit) : null,
            duedays: this._ticket.duedays,
            flags: this._ticket.flags
        };

        if (this.IsVolatile){   // avoid refreshing the isolated item
            _change['updated'] = this._ticket.updated ? this.dateStrToMysql(this._ticket.updated) : null;
        }

        // Campo meta _reopen (prefijo '_' como _actn/_uuid): el server lo lee en
        // prev_complete_ticket_specific para permitir el PD->AC deliberado y lo
        // descarta antes de escribir en DB. One-shot para no arrastrarlo a commits
        // siguientes (que ya serian AC->AC y no dispararian la proteccion).
        if (this._reopenIntent){
            _change['_reopen'] = 1;
            this._reopenIntent = false;
        }

        return _change;
    }

    protected get Depend() {
        return {
            place: { item: this.place, relation_info: { to: 'tickets', by: 'place' } },     // this[by -> 'place'][to -> 'tickets'] => this
            session:{ item: this.session, relation_info: { to: null, by: 'session' } },     // no relation to this in this[by -> 'session']
            // waiter NO va en Depend: se envía como campo (this._ticket.waiter) para
            // no notificar/propagar la fila USER del camarero (fuga de PII).
            qrcode: { item: this.qrcode, relation_info: { to: 'tickets', by: 'qrcode' } },  // this[by -> 'qrcode'][to -> 'tickets'] => this
            account: { item: this.account, relation_info: { to: null, by: 'account' } },    // no relation to this in this[by -> 'account']
            invceac: { item: this.invceac, relation_info: { to: null, by: 'invceac' } }     // no relation to this in this[by -> 'invceac']
        };
    }

    protected get Children(){
        let _children = [];

        for(let _item of this.products){
            _children.push(_item)
        }

        for(let _item of this.requests){
            _children.push(_item);
        }

        for(let _item of this.offers){
            _children.push(_item)
        }

        for(let _item of this.extras){
            _children.push(_item)
        }

        for(let _item of this.discounts){
            _children.push(_item)
        }

        for(let _item of this.invoices){
            _children.push(_item)
        }

        for(let _item of this.changes){
            _children.push(_item);
        }

        for(let _item of this.mixed){
            _children.push(_item);
        }

        for(let _item of this.audits){
            _children.push(_item)
        }

        return _children;
    }

    /****************************/
    /* DATA OBJECT              */
    /****************************/
    
    /** Commit en vuelo o write en cola hacia el server — NO el estado normal
     *  de un borrador (ToInsert en ticket/productos). Bloquear por ToInsert
     *  dejaba MANDAR/METÁLICO/TARJETA siempre deshabilitados al crear comanda. */
    protected _hasServerSyncInFlight(): boolean {
        if (this._inCommit){
            return true;
        }

        const _uuid = (this as any)._uuid;
        if (this.data?.sync?.HasQueuedChange('TICKET', _uuid, this.objid)){
            return true;
        }

        return false;
    }

    protected _flagIsSet(flagsStr: string, flag: number): boolean {
        return !!(flagsStr && flagsStr[flag] == '1');
    }

    private _patchData(_ticket: _Ticket){
        let _toUpdate = false;

        // Double-check de reabrir: snapshot del estado de pagado LOCAL antes del
        // merge. Si la fuente entrante es un PD stale (server aun no ha persistido
        // el reabrir) y revierte el AC local, lo restauramos al final.
        let _guardReopen = (this._reopenGuardUntil > Date.now());
        let _prevStatus  = this._ticket.status;
        let _prevPaidon  = this._ticket.paidon;
        let _prevPaidby  = this._ticket.paidby;
        let _prevPaidin  = this._ticket.paidin;
        let _prevAccount = this._ticket.account;
        let _prevPayment = this._ticket.payment;
        let _prevPrice   = this._ticket.price;
        let _prevFlags   = this._ticket.flags;

        _toUpdate = this.patchValue(this._ticket, 'place', _ticket['place']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'session', _ticket['session']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'waiter', _ticket['waiter']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'paidby', _ticket['paidby']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'paidon', _ticket['paidon']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'paidin', _ticket['paidin']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'qrcode', _ticket['qrcode']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'price', _ticket['price']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'refund', _ticket['refund']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'taxes', _ticket['taxes']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'orderno', _ticket['orderno']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'series', _ticket['series']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'invceno', _ticket['invceno']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'elapsed', _ticket['elapsed']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'comments', _ticket['comments']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'payment', _ticket['payment']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'transaction', _ticket['transaction']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'account', _ticket['account']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'invceac', _ticket['invceac']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'given', _ticket['given']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'flags', _ticket['flags']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'tmcommit', _ticket['tmcommit']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'duedays', _ticket['duedays']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'diners', _ticket['diners']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'status', _ticket['status']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'sentto', _ticket['sentto']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'intill', _ticket['intill']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'pmixed', _ticket['pmixed']) || _toUpdate;
        _toUpdate = this.patchValue(this._ticket, 'device', _ticket['device']) || _toUpdate;

        // Double-check de reabrir: si dentro de la ventana de reabrir una fuente
        // STALE acaba de revertir el AC local a PD, restauramos TODO el estado de
        // pagado al reabierto. El PD del server es obsoleto (aun no ha visto el
        // reabrir); la ventana expira sola, asi que un pago legitimo posterior si
        // entra.
        if (_guardReopen && _prevStatus == 'AC' && this._ticket.status == 'PD'){
            this.patchValue(this._ticket, 'status', _prevStatus);
            this.patchValue(this._ticket, 'paidon', _prevPaidon);
            this.patchValue(this._ticket, 'paidby', _prevPaidby);
            this.patchValue(this._ticket, 'paidin', _prevPaidin);
            this.patchValue(this._ticket, 'account', _prevAccount);
            this.patchValue(this._ticket, 'flags', _prevFlags);
            _toUpdate = true;
        }

        // Guard: sync stale que revierte un pago local (PD→AC) o un importe
        // ya calculado. Tras payment=... el status pasa a PD antes de que el
        // server confirme; un refresh race (modify+pay, 504) puede traer AC con
        // importe viejo y dejar la comanda "abierta" otra vez.
        const _wasPaid = (['PD', 'PP'].includes(_prevStatus))
                       || !!_prevPayment
                       || this._flagIsSet(_prevFlags, this.TicketFlags.TicketPaid);
        const _nowPaid = (['PD', 'PP'].includes(this._ticket.status))
                      || !!this._ticket.payment
                      || this._GetFlagValue(this.TicketFlags.TicketPaid);

        if (_wasPaid && !_nowPaid && !_guardReopen){
            this.patchValue(this._ticket, 'status', _prevStatus);
            this.patchValue(this._ticket, 'payment', _prevPayment);
            this.patchValue(this._ticket, 'paidon', _prevPaidon);
            this.patchValue(this._ticket, 'paidby', _prevPaidby);
            this.patchValue(this._ticket, 'paidin', _prevPaidin);
            this.patchValue(this._ticket, 'account', _prevAccount);
            this.patchValue(this._ticket, 'flags', _prevFlags);
            if (_prevPrice != null){
                this.patchValue(this._ticket, 'price', _prevPrice);
            }
            _toUpdate = true;
        }

        // Guard: importe menor del server mientras hay un commit/sync en vuelo.
        if (this._hasServerSyncInFlight()
            && (_prevPrice != null)
            && (this._ticket.price != null)
            && (Number(this._ticket.price) < Number(_prevPrice))){
            this.patchValue(this._ticket, 'price', _prevPrice);
            _toUpdate = true;
        }

        return _toUpdate;
    }

    set Data(_ticket: _Ticket){
        this.patchValue(this._ticket, 'created', _ticket['created']);
        this.patchValue(this._ticket, 'updated', _ticket['updated']);
        
        if (this._patchData(_ticket)){
            this.ToUpdate = true;
        }
    }

    get Info() :_TicketData {
        return this._ticket;
    }

    set Info(value){
        this.DoPatchValues(value);
    }

    /*
        Establece la mesa de la comanda MANTENIENDO EL ENLACE INVERSO.

        SetChild solo crea la referencia comanda -> mesa. La coleccion 'tickets'
        de la mesa (de donde sale QrCode.IsBusy, o sea el verde de la mesa) se
        rellenaba unicamente con el array 'tickets' de la propia fila QRCODE del
        sync. Si en un lote llegaba la fila TICKET pero no la del QRCODE, la
        comanda aparecia en el listado (que lee place.tickets) y la mesa se
        quedaba gris hasta que algo volviese a tocar ese QRCODE (#1470).

        Esto cubre el camino del sync. Los caminos locales ya los cubre Place:
        una comanda entra en la mesa por Place.AddTicket y sale por DelTicket
        (que enganchan / desenganchan el QRCODE), y cambiar de mesa a mano lo
        hace OnTicketTable. Deliberadamente NO se engancha desde el setter
        'qrcode': eso meteria en la mesa los borradores del carro, que no estan
        en place.tickets, y cambiaria el "continuar pedido" del invitado.
    */
    private _SetTable(_qrcode: QrCode | null){   // null = comanda de barra (sin mesa)
        let _prev = this.qrcode;

        this.SetChild('qrcode', _qrcode, 'qrcode');

        let _table = this.qrcode;   // SetChild resuelve la instancia del storage
        if (_prev == _table){
            return;     // la mesa no ha cambiado
        }

        if (this.IsVolatile || this.CopyOf){
            return;     // copias e instancias de informes no van en la mesa
        }

        let _self = this as unknown as Ticket;      // TicketData es la base de Ticket
        if (_prev){
            _prev.DelTicket(_self);
        }

        if (_table){
            _table.AddTicket(_self);
        }
    }

    // Campos escalares y relaciones que componen el ESTADO DE PAGO del ticket. Si hay
    // un lote sin confirmar (_inCommitSince; base.OnUpdate ya evaluo
    // _EchoConfirmsCommit y lo habria puesto a 0 si esta fila confirmara lo enviado),
    // una fila que no lo confirma es anterior o de otro dispositivo y NO puede pisar
    // el estado de pago local pendiente. Caso: cobro a cuenta -> tarjeta, lote
    // rechazado, llega una lectura aun "a cuenta": sin esto se conservaba PAYCARD
    // pero reaparecian la cuenta y las sesiones antiguas (SetChild va antes y despues
    // de _patchData) y el siguiente lote enviaba esa combinacion incoherente. El
    // estado local se conserva entero hasta el eco propio o el reenvio del rescate.
    // Incluye la IDENTIDAD FISCAL del cobro (series/invceno, que asigna el cliente al
    // cobrar, e importe): una lectura del ticket aun abierto (serie de pedido) pisaba
    // la numeracion y el reenvio llevaba una cabecera distinta del cambio fiscal ya
    // firmado.
    private static readonly _PAY_FIELDS = ['status', 'payment', 'paidon', 'given', 'flags', 'series', 'invceno', 'price', 'taxes', 'paidby', 'paidin', 'account', 'transaction', 'invceac'];
    private static readonly _PAY_CHILDREN = ['paidby', 'paidin', 'account', 'transaction', 'invceac'];

    private _PendingPaymentSnapshot(){
        if ((this._inCommitSince == 0) || (this._reopenGuardUntil > Date.now())){
            return null;
        }
        let _paid = (['PD', 'PP'].includes(this._ticket.status))
                 || !!this._ticket.payment
                 || this._GetFlagValue(this.TicketFlags.TicketPaid);
        if (!_paid){
            return null;
        }
        let _snap = { fields: {}, children: {} };
        for (let _field of Ticket._PAY_FIELDS){
            _snap.fields[_field] = this._ticket[_field];
        }
        for (let _rel of Ticket._PAY_CHILDREN){
            _snap.children[_rel] = this._children[_rel] || null;
        }
        return _snap;
    }

    private _RestorePendingPayment(_snap){
        let _kept = false;
        for (let _field of Ticket._PAY_FIELDS){
            _kept = this.patchValue(this._ticket, _field, _snap.fields[_field]) || _kept;
        }
        for (let _rel of Ticket._PAY_CHILDREN){
            let _prev = _snap.children[_rel];
            if ((this._children[_rel] || null) !== _prev){
                this.SetChild(_rel, _prev, _rel);
                _kept = true;
            }
        }
        if (_kept){
            console.warn('[TICKET] fila no confirmadora con cobro pendiente: se conserva el estado de pago local (campos y relaciones)', { objid: this.objid, payment: _snap.fields['payment'], status: _snap.fields['status'] });
            this.ToUpdate = true;
        }
    }

    private DoPatchValues(_ticket: _Ticket){
        // Estado de pago local pendiente de confirmar: snapshot antes de tocar nada
        // (SetChild de paidby/paidin va antes de _patchData, el de account despues).
        let _pendingPay = this._PendingPaymentSnapshot();

        // Children que OnPayment/IsPaidProcessOwner necesitan ANTES de
        // _patchData: el patch de status='PD' dispara OnPayment sincrono.
        if (_ticket['session']){   // update children: 'session'
            let _objid = _ticket['session'].toString();
            this.SetChild('session', new Session(_objid, this.data), 'session')
        }
        else {
            this.SetChild('session', null, 'session');
        }

        if (_ticket['waiter']){    // update children: 'waiter' (USER)
            let _objid = _ticket['waiter'].toString();
            this.SetChild('waiter', new User(_objid, this.data), 'waiter')
        }
        else {
            this.SetChild('waiter', null, 'waiter');
        }

        if (_ticket['paidby']){   // update children: 'paidby'
            let _objid = _ticket['paidby'].toString();
            this.SetChild('paidby', new Session(_objid, this.data), 'paidby')
        }
        else {
            this.SetChild('paidby', null, 'paidby');
        }

        if (_ticket['paidin']){   // update children: 'paidin'
            let _objid = _ticket['paidin'].toString();
            this.SetChild('paidin', new Session(_objid, this.data), 'paidin')
        }
        else {
            this.SetChild('paidin', null, 'paidin');
        }

        if (_ticket['place']){     // update children: 'place'
            let _objid = _ticket['place'].toString();
            this.SetChild('place', new Place(_objid, this.data, this._objoptions), 'place')
        }
        else {
            this.SetChild('place', null, 'place');
        }

        if (_ticket['qrcode']){     // update children: 'table' (null for bar tickets)
            let _objid = _ticket['qrcode'].toString();
            this._SetTable(new QrCode(_objid, this.data, this._objoptions));
        }
        else {
            this._SetTable(null);
        }

        this._patchData(_ticket);

        if (_ticket['transaction']) {    // update children: 'transaction'
            let _objid = _ticket['transaction'].toString();        
            this.SetChild('transaction', new Payment(_objid, this.data, this._objoptions), 'transaction')
        }
        else {
            this.SetChild('transaction', null, 'transaction');
        }  
	
        if (_ticket['account']){    // update children: 'account'
            let _objid = _ticket['account'].toString();        
            this.SetChild('account', new PayAccount(_objid, this.data, this._objoptions), 'account')
        }
        else {
            this.SetChild('account', null, 'account');
        }

        if (_ticket['invceac']){    // update children: 'invceac'
            let _objid = _ticket['invceac'].toString();        
            this.SetChild('invceac', new AccountInvoice(_objid, this.data, this._objoptions), 'invceac')
        }
        else {
            this.SetChild('invceac', null, 'invceac');
        }

        if (_ticket['intill']){     // update children: 'intill'
            let _objid = _ticket['intill'].toString();        
            this.SetChild('intill', new Audit(_objid, this.data, this._objoptions), 'intill')
        }
        else {
            this.SetChild('intill', null, 'intill');
        }        

        if (_pendingPay){
            this._RestorePendingPayment(_pendingPay);
        }
    }

    private _ddbb(info): _TicketData {
        let _ticket: _TicketData = {
            objid: info['objid'] ? parseInt(info['objid']) : null,
            created: new Date(Date.parse(this.mysqlToDateStr(info['created']))),
            updated: new Date(Date.parse(this.mysqlToDateStr(info['updated']))),
            status: info['status'],
            place: info['place'] ? parseInt(info['place']) : null,
            session: info['session'] ? parseInt(info['session']) : null,
            waiter: info['waiter'] ? parseInt(info['waiter']) : null,
            paidby: info['paidby'] ? parseInt(info['paidby']) : null,
            paidon: info['paidon'] ? new Date(Date.parse(this.mysqlToDateStr(info['paidon']))) : null,
            paidin: info['paidin'] ? parseInt(info['paidin']) : null,            
            qrcode: info['qrcode'] ? parseInt(info['qrcode']) : null,
            price: info['price'] ? parseFloat(info['price']) : null,
            refund: info['refund'] ? parseFloat(info['refund']) : 0,
            taxes: info['taxes'] ? parseFloat(info['taxes']) : null,
            orderno: info['orderno'],
            series: info['series'],
            invceno: info['invceno'],
            elapsed: info['elapsed'] ? parseInt(info['elapsed']) : 0,
            comments: info['comments'],
            payment: info['payment'],
            account: info['account'] ? parseInt(info['account']) : null,
            invceac: info['invceac'] ? parseInt(info['invceac']) : null,
            given: info['given'] ? parseFloat(info['given']) : null,
            transaction: info['transaction'] ? parseInt(info['transaction']) : null,
            tmcommit: info['tmcommit'] ? new Date(Date.parse(this.mysqlToDateStr(info['tmcommit']))) : null,
            duedays: info['duedays'] ? parseInt(info['duedays']) : 30,
            flags: info['flags'],
            diners: info['diners'] ? parseInt(info['diners']) : null,

            client: ('client' in info) ? info['client'] : null,
            sentto: ('sentto' in info) ? info['sentto'] : null,
            intill: ('intill' in info) ? info['intill'] : null,
            pmixed: ('pmixed' in info) ? info['pmixed'] : null,
            device: ('device' in info) ? info['device'] : null,
        };

        return _ticket;
    }

    protected _OnUpdate(info){
        if (info['status'] == 'PA'){
            console.log("[WARNING] Receiving ticket in pending activation state");
            return;     // do not accept tickets if pending activation (should not get into here)
        }

        // Restaurar el `_uuid` PERSISTIDO de la fila. El getter (base.ts) genera
        // uno FRESCO por instancia si `__uuid` está vacío, así que un ticket
        // cargado en OTRO dispositivo —o recargado de la DB— tendría un `_uuid`
        // distinto al de su fila y rompía TODO lo que se clavea por `_uuid`
        // (abono de tarjeta cross-device, stash de paytef, HasQueuedChange...).
        // En el dispositivo que lo creó, `info['_uuid']` es el mismo local (el
        // server lo conserva: "front-generated UUID for idempotent inserts") →
        // no-op. La guarda evita tocarlo si no viene en el payload.
        if (info['_uuid']){
            this._uuid = info['_uuid'];
        }

        let _ticket = this._ddbb(info);
        
        this.patchValue(this._ticket, 'objid', _ticket['objid']);
        this.patchValue(this._ticket, 'created', _ticket['created']);
        this.patchValue(this._ticket, 'updated', _ticket['updated']);
        this.DoPatchValues(_ticket);

        if (info['products']){   // update children: 'products'
            this.SetChildren <TicketProduct> (info['products'], 'products', TicketProduct, 'ticket');
        }

        if (info['offers']){   // update children: 'offers'
            this.SetChildren <TicketOffer> (info['offers'], 'offers', TicketOffer, 'ticket');
        }

        if (info['extras']){   // update children: 'extras'
            this.SetChildren <TicketExtra> (info['extras'], 'extras', TicketExtra, 'ticket');
        }

        if (info['discounts']){   // update children: 'discounts'
            this.SetChildren <TicketDiscount> (info['discounts'], 'discount', TicketDiscount, 'ticket');
        }

        if (info['changes']){   // update children: 'changes'
            this.SetChildren <TicketChange> (info['changes'], 'changes', TicketChange, 'ticket');
        }

        if (info['mixed']){   // update children: 'mixed'
            this.SetChildren <TicketMixedPayment> (info['mixed'], 'mixed', TicketMixedPayment, 'ticket');
        }

        if (info['audit']){   // update children: 'audit'
            this.SetChildren <TicketAudit> (info['audit'], 'audit', TicketAudit, 'ticket');
        }

        if (info['invoice']){   // update children: 'invoice'
            this.SetChildren <TicketInvoice> (info['invoice'], 'invoice', TicketInvoice, 'ticket');
        }
    }
}

function sameArray(a1, a2){
    if (a1.length == a2.length){
        return a1.every((item) => {
            return a2.includes(item);
        });
    }
    return false;
}

/*******************************/
/* TICKET GROUPED PRODUCTS     */
/*******************************/

export interface CartProduct {
    requested: Array<TicketProduct>;
    status: string;
    product: Product;
    issep: boolean;
    sort: number;
    group: number;
    offer: Offer;
    fixed: boolean;
    charge: number;
    taxrate: number;
    market: number,
    weight: number,
    comments: string;
    events: { [key: string] : boolean; };
    options: Array<ProductOption>;
    totalprice: number;             // the total product price
    fixedprice: number;             // the fixed product price (when updated for the ticket)
    offerprice: number;             // the product price when offer is applied

    onRefresh: Subject<any>;        // notifies changes on this cart product
};

class CartProducts {
    private _cartproducts: Array<CartProduct> = [];
    get Products(){
        return this._cartproducts;
    }

    constructor(_cartitems : Array <CartItem> = null){
        if (!_cartitems){
            return;
        }

        for(let _item of _cartitems){
            if (_item.product){
                this._cartproducts.unshift(_item.product);
            }
        }
    }
        
    OnRefresh(_ticketproducts){
        this._updateProducts(this._GetCartProducts(_ticketproducts));
    }
    
    private _findCartProduct(needle: CartProduct, haystack: Array <CartProduct>){
        for(let _idx = 0; _idx < haystack.length; _idx++){
            let _same = true;

            if (needle.product != haystack[_idx].product){
                _same = false;  // not same product
            }

            if (needle.group != haystack[_idx].group){
                _same = false;  // not same grouping
            }

            if (needle.taxrate !=  haystack[_idx].taxrate){
                _same = false;  // not same tax rate
            }

            if (needle.market !=  haystack[_idx].market){
                _same = false;  // not same market price
            }
            
            if (needle.weight !=  haystack[_idx].weight){
                _same = false;  // not same weight
            }

            if (needle.offer != haystack[_idx].offer){
                _same = false;  // not same offer
            }

            if ((needle.fixed != haystack[_idx].fixed) || (needle.fixedprice != haystack[_idx].fixedprice)) {
                _same = false;  // not same fixed
            }

            if (_same){
                for(let _event in needle.events){
                    if (needle.events[_event] != haystack[_idx].events[_event]){
                        _same = false;  // not same events
                    }
                }
            }

            // check for one requested
            if (_same){
                _same = (needle.requested.some(
                (_requested) => {
                    return (haystack[_idx].requested.includes(_requested));
                }));        
            }
            
            if (_same){    // the requested item found in this cart product
                return _idx;    
            }
        }

        return -1;  // not found
    }

    private _updateProducts(_cartproducts){
        for(let _idx = this._cartproducts.length - 1; _idx >= 0; _idx--){
            if (this._findCartProduct(this._cartproducts[_idx], _cartproducts) == -1){
                this._cartproducts.splice(_idx, 1);     // delete
            }
        }

        // _claimed: grupos de la lista persistente que este pase reconcilia
        // (destino de un update, o recien insertados). Los que sobreviven al
        // borrado inicial pero NO quedan reclamados son fantasmas y se podan
        // abajo — ver el comentario de la poda.
        let _claimed = new Set();
        for(let _idx = _cartproducts.length - 1; _idx >= 0; _idx--){
            let _pos = this._findCartProduct(_cartproducts[_idx], this._cartproducts);
            if (_pos == -1) {   // insert
                this._cartproducts.push(_cartproducts[_idx]);
                _claimed.add(_cartproducts[_idx]);
            }
            else {  // update
                let _dst = this._cartproducts[_pos];
                let _src = _cartproducts[_idx];
                _claimed.add(_dst);

                // Detectar cambio de contenido, no solo de longitud del grupo.
                // El fast path in-place de CartSet muta options/comments del
                // MISMO TicketProduct sin cambiar cuantos hay — antes solo
                // comparabamos length y el CartProduct dst se quedaba con
                // options/comments obsoletos y sin emitir refresh.
                let _optsChanged = _src.options.length != _dst.options.length
                    || _src.options.some((_o, _i) => _o !== _dst.options[_i]);
                let _changed = _src.requested.length != _dst.requested.length
                    || _src.comments != _dst.comments
                    || _src.fixed != _dst.fixed
                    || _src.charge != _dst.charge
                    || _src.market != _dst.market
                    || _src.weight != _dst.weight
                    || _optsChanged;

                _dst.requested = _src.requested;
                _dst.comments = _src.comments;
                _dst.options = _src.options;
                _dst.fixed = _src.fixed;
                _dst.charge = _src.charge;
                _dst.market = _src.market;
                _dst.weight = _src.weight;
                _dst.taxrate = _src.taxrate;
                _dst.status = _src.status;
                _dst.events = _src.events;

                if (_changed){
                    _dst.onRefresh.next();
                }
            }
        }

        // Poda de grupos fantasma: cuando DOS grupos viejos comparten una
        // referencia con el MISMO grupo nuevo (p.ej. modificar el 2º producto
        // a la misma variante que el 1º -> A=[tpX] y B=[tpY] colapsan en
        // D=[tpX,tpY]), el bucle de borrado conserva ambos (cada uno comparte
        // una referencia con D por el `some` de _findCartProduct) pero solo UNO
        // recibe el update; el otro queda huerfano apuntando a un TicketProduct
        // ya pintado en el grupo fusionado -> "linea de mas". _GetCartProducts
        // ya da la particion correcta, asi que eliminamos todo grupo que no
        // haya sido reclamado (update/insert) en este pase.
        for(let _idx = this._cartproducts.length - 1; _idx >= 0; _idx--){
            if (!_claimed.has(this._cartproducts[_idx])){
                this._cartproducts.splice(_idx, 1);
            }
        }

        // sort the cartproducts (separator on top)
        this._cartproducts.sort((a, b) => {
            if (a.sort == b.sort){
                return (a.issep) ? -1 : 1;
            }

            return a.sort - b.sort;
        });

        let _sort = 0;
        for (let _cartproduct of this._cartproducts){
            _cartproduct.sort = _sort;
            for (let _ticketproduct of _cartproduct.requested){
                _ticketproduct.sort = _sort;
            }
            _sort = _sort + 1;
        }

        // remove separator from list top (allow on bottom)
        if ((this._cartproducts.length > 0) && (this._cartproducts[0].issep)){
            this._cartproducts.splice(0, 1);
        }

        // calculate final price, and price to be charged (if offers are applied)
        for (let _cartproduct of this._cartproducts){
            _cartproduct.fixedprice = 0;
            _cartproduct.totalprice = 0;
            _cartproduct.offerprice = 0;

            for(let requested of _cartproduct.requested){
                _cartproduct.fixedprice += requested.FixedPrice;
                _cartproduct.totalprice += requested.TotalPrice;
                _cartproduct.offerprice += requested.OfferPrice;
            }

            _cartproduct.fixedprice = Math.round(_cartproduct.fixedprice * 100) / 100;
            _cartproduct.totalprice = Math.round(_cartproduct.totalprice * 100) / 100;
            _cartproduct.offerprice = Math.round(_cartproduct.offerprice * 100) / 100;
        }        
    }

    MoveCartProduct(target, delta){
        this._cartproducts.sort((a, b) => {
            return a.sort - b.sort;
        });

        let _idx = this._cartproducts.indexOf(target);
        if (_idx === -1) {
            return; // target not found
        }

        let _tsort = _idx + delta;
        _tsort = Math.min(this._cartproducts.length - 1, _tsort);
        _tsort = Math.max(0, _tsort);

        this._cartproducts.splice(_idx, 1);
        this._cartproducts.splice(_tsort, 0, target);

        // sort the cart products
        let _sort = 0;
        for (let _cartproduct of this._cartproducts){
            _cartproduct.sort = _sort;
            for (let _ticketproduct of _cartproduct.requested){
                _ticketproduct.sort = _sort;
            }
            _sort = _sort + 1;
        }
    }

    private _findProduct(needle, haystack){
        for (let _cartproduct of haystack){
            if (needle.product){
                if (needle.product.objid != _cartproduct.product?.objid){
                    continue;   // not same product
                }    

                if (needle.product.price != _cartproduct.product?.price){
                    continue;   // do not join diferent price products (various)
                }    

                if (needle.product.type != _cartproduct.product?.type){
                    continue;   // do not join diferent type products (various)
                }    

                if (needle.product.IsWeighted){
                    continue;   // do not join weighted products
                }    
            }
            else {
                if (_cartproduct.product){
                    continue;   // do not join with separators
                }
            }

            if (needle.group != _cartproduct.group){
                continue;   // not same group
            }

            if (needle.fixed != _cartproduct.fixed){
                continue;   // not same fixed
            }

            if (needle.market != _cartproduct.market){
                continue;   // not same market price
            }

            if (needle.taxrate != _cartproduct.taxrate){
                continue;   // not same tax rate
            }

            if (needle.status != _cartproduct.status){
                continue;   // not same status
            }
            
            if (needle.comments != _cartproduct.comments){
                continue;   // not same comments
            }

            if ((needle.offer ? needle.offer.offer : null) != _cartproduct.offer){
                continue;   // not same offer applied
            }

            let _pr1_options = [];
            for(let _option of needle.options){
                _pr1_options.push(_option.option);
            }

            let _pr2_options = [];
            for(let _option of _cartproduct.requested[0].options){
                _pr2_options.push(_option.option);
            }

            if (!sameArray(_pr1_options, _pr2_options)){
                continue;   // not same options
            }

            let _sameevents = true;
            for(let _event in needle.events){
                if (_event == 'CC'){
                    continue;   // do not consider 'CC' status
                }

                if (needle.events[_event] != _cartproduct.requested[0].events[_event]){
                    _sameevents = false;   
                }
            }

            if (!_sameevents){
                continue;   // not same events
            }

            return _cartproduct;
        }
        
        return null;    // not found
    }

    private _GetCartProducts(ticketproducts){
        let _products: Array<CartProduct> = [];

        ticketproducts.sort((a, b) => {
            if ((a.sort !== null) && (b.sort !== null)){
                return a.sort - b.sort;
            }

            if (a.sort !== null){
                return -1;
            }

            if (b.sort !== null){
                return 1;
            }

            return (a.created.getTime() - b.created.getTime());
        });

        let _ltsep = 0;     // group the separators
        let _group = 0;     // separate the products

        for (let _ticketproduct of ticketproducts){
            
            // increase the belonging group whenever a separator is found
            if (_ticketproduct.Separator){
                if (_ltsep == 0){
                    _group = _group + 1;
                }

                _ltsep = _ltsep + 1; 
            }
            else {
                _ltsep = 0;     // last is not a separator
            }

            _ticketproduct.group = _group;

            let _cartproduct: CartProduct = this._findProduct(_ticketproduct, _products);
            if (_cartproduct){    // this product is already available
                _cartproduct.requested.push(_ticketproduct);
            }
            else {  // this is a new product (create and add to list)
                let _lastsort = _products.length;

                _cartproduct = {
                    product: _ticketproduct.product,
                    offer: _ticketproduct.offer ? _ticketproduct.offer.offer : null,
                    fixed: _ticketproduct.fixed,
                    charge: _ticketproduct.charge,
                    market: _ticketproduct.market,
                    weight: _ticketproduct.weight,
                    taxrate: _ticketproduct.taxrate,
                    status: _ticketproduct.status,
                    comments: _ticketproduct.comments,
                    events: _ticketproduct.events,
                   
                    requested: [ _ticketproduct ],
                    issep: _ticketproduct.Separator,
                    sort: (_ticketproduct.sort === null) ? _lastsort : _ticketproduct.sort,
                    group: _ticketproduct.group,
                    options: [],
                    fixedprice: 0,
                    totalprice: 0,
                    offerprice: 0,

                    onRefresh: new Subject<any>()
                };

                for(let _ticketoption of _ticketproduct.options){
                    _cartproduct.options.push(_ticketoption.option);
                }
                                
                _products.push(_cartproduct);                      
            }
        }

        // calculate final price, and price to be charged (if offers are applied)
        for (let _cartproduct of _products){
            _cartproduct.totalprice = 0;
            _cartproduct.offerprice = 0;

            for(let requested of _cartproduct.requested){
                _cartproduct.totalprice += requested.TotalPrice;
                _cartproduct.offerprice += requested.OfferPrice;
            }

            _cartproduct.totalprice = Math.round(_cartproduct.totalprice * 100) / 100;
            _cartproduct.offerprice = Math.round(_cartproduct.offerprice * 100) / 100;
        }

        return _products;
    }
}

/*******************************/
/* TICKET GROUPED OFFERS       */
/*******************************/

export interface CartOfferProduct {
    product: Product,
    active: Array<TicketProduct>,
    refund: Array<TicketProduct>,
    chargeprice: number,
    refundprice: number
}

export interface CartOffer {
    requested: Array<TicketOffer>;
    status: string;
    offer: Offer;
    fixed: boolean;
    products: Array<CartOfferProduct>
    offerprice: number;             // the original offer price
    fixedprice: number;             // the updated offer price (when updated for this ticket)
    singleprice: number;            // the price for one offer contined in this group
    totalprice: number;             // the offer price with products
    chargeprice: number;            // the final offer charge price (when updated for this ticket)
    refundprice: number;            // the offer refund price (when some products are returned)

    onRefresh: Subject<any>;        // notifies changes on this cart product
};

class CartOffers {
    private _cartoffers: Array<CartOffer> = [];
    get Offers(){
        return this._cartoffers;
    }

    constructor(_cartitems : Array <CartItem> = null){
        if (!_cartitems){
            return;
        }

        for(let _item of _cartitems){
            if (_item.offer){
                this._cartoffers.unshift(_item.offer);
            }
        }
    }
    
    OnRefresh(_ticketoffers){
        let _activeoffers = [];
        
        for(let _offer of _ticketoffers){
            if (_offer.IsValid){
                _activeoffers.push(_offer);
            }
        }

        this._updateOffers(this._GetCartOffers(_activeoffers));        
    }

    private _findCartOffer(needle: CartOffer, haystack: Array <CartOffer>){
        for(let _idx = 0; _idx < haystack.length; _idx++){
            let _same = true;

            if (needle.offer != haystack[_idx].offer) {
                _same = false;      // not same offer
            }

            if (_same){
                _same = (needle.requested.length == haystack[_idx].requested.length) && (needle.requested.every(
                (_requested) => {
                    return (_requested.IsValid) && (haystack[_idx].requested.indexOf(_requested) != -1);
                }));        
            }
            
            if (_same){    // the requested item found in this cart offer
                return _idx;    
            }
        }

        return -1;  // not found
    }

    private _updateOffers(_cartoffers){
        for(let _idx = this._cartoffers.length - 1; _idx >= 0; _idx--){
            if (this._findCartOffer(this._cartoffers[_idx], _cartoffers) == -1){
                this._cartoffers.splice(_idx, 1);     // delete
            }
        }

        for(let _idx = _cartoffers.length - 1; _idx >= 0; _idx--){
            let _pos = this._findCartOffer(_cartoffers[_idx], this._cartoffers);
            if (_pos == -1) {   // insert
                this._cartoffers.push(_cartoffers[_idx]);
            }
            else {  // update
                let _src = _cartoffers[_idx];
                let _dst = this._cartoffers[_pos];

                let _same = true;
                for(let _property of Object.keys(_dst)){
                    if (_dst[_property] != _src[_property]){
                        _same = false;
                    }

                    _dst[_property] = _src[_property];
                }

                if (!_same){
                    this._cartoffers[_pos].onRefresh.next();
                }
            }
        }

        // calculate final price, and price to be charged (if offers are applied)
        for (let _cartoffer of this._cartoffers){
            _cartoffer.offerprice = 0;
            _cartoffer.totalprice = 0;
            _cartoffer.singleprice = 0;
            _cartoffer.chargeprice = 0;            
            _cartoffer.refundprice = 0;

            if (_cartoffer.requested.length > 0){
                _cartoffer.singleprice = _cartoffer.requested[0].ChargePrice;
            }

            for(let requested of _cartoffer.requested){
                _cartoffer.offerprice += requested.OfferPrice;
                _cartoffer.totalprice += requested.TotalPrice;
                _cartoffer.chargeprice += requested.ChargePrice;
                _cartoffer.refundprice += requested.refund;
            }

            _cartoffer.offerprice = Math.round(_cartoffer.offerprice * 100) / 100;
            _cartoffer.totalprice = Math.round(_cartoffer.totalprice * 100) / 100;
            _cartoffer.chargeprice = Math.round(_cartoffer.chargeprice * 100) / 100;
            _cartoffer.refundprice = Math.round(_cartoffer.refundprice * 100) / 100;
        }
    }
    
    private _findOffer(needle, haystack){
        if (!needle.offer){
            return null;   // offer load is pending
        }

        for (let _cartoffer of haystack){
            let _ticketoffer = _cartoffer.requested[0];

            if (needle.offer.objid != _ticketoffer.offer.objid){
                continue;   // not same offer
            }

            if (needle.fixed != _ticketoffer.fixed){
                continue;   // not same offer
            }

            if (needle.status != _ticketoffer.status){
                continue;   // not same status
            }

            if (needle.charge != _ticketoffer.charge){
                continue;   // not same charge
            }

            return _cartoffer;
        }
                                      
        return null;    // not found
    }
 
    private _AddTicketOffer(_cartoffer, _ticketoffer){
        for(let _ticketproduct of _ticketoffer.ticket.products){
            if (_ticketproduct.offer == _ticketoffer){
                let _cartofferproduct = null;
                
                for(let _product of _cartoffer.products){
                    if (_product.product == _ticketproduct.product){
                        if (_ticketproduct.status == 'AC'){
                            _product.active.push(_ticketproduct);
                            _product.chargeprice += _ticketproduct.OfferPrice;
                        }

                        if (_ticketproduct.status == 'UN'){
                            _product.refund.push(_ticketproduct);
                            _product.refundprice += _ticketproduct.OfferPrice;
                        }

                        _cartofferproduct = _product;
                    }
                }

                if (!_cartofferproduct){    // new product
                    _cartofferproduct = {
                        product: _ticketproduct.product,
                        active: [],
                        refund: [],
                        chargeprice: 0,
                        refundprice: 0
                    };

                    if (_ticketproduct.status == 'AC'){
                        _cartofferproduct.active.push(_ticketproduct);
                        _cartofferproduct.chargeprice += _ticketproduct.OfferPrice;
                    }

                    if (_ticketproduct.status == 'UN'){
                        _cartofferproduct.refund.push(_ticketproduct);
                        _cartofferproduct.refundprice += _ticketproduct.OfferPrice;
                    }

                    _cartoffer.products.push(_cartofferproduct);
                }
            }
        }

        _cartoffer.requested.push(_ticketoffer);
    }

    private _GetCartOffers(ticketoffers){
        let _offers : Array<CartOffer> = [];
        
        let _ticketoffers = ticketoffers.sort((a, b) => {
            return (a.created.getTime() - b.created.getTime());
        });

        for (let _ticketoffer of _ticketoffers.reverse()){
            let _cartoffer: CartOffer = this._findOffer(_ticketoffer, _offers);
            if (!_cartoffer){   
                _cartoffer = {
                    offer: _ticketoffer.offer,
                    fixed: _ticketoffer.fixed,
                    status: _ticketoffer.status,
                    requested: [],
                    products: [],
                    offerprice: 0,
                    fixedprice: _ticketoffer.price,
                    totalprice: 0,
                    singleprice: 0,
                    chargeprice: 0,
                    refundprice: 0,

                    onRefresh: new Subject<any>()
                };

                _offers.push(_cartoffer); 
            }

            this._AddTicketOffer(_cartoffer, _ticketoffer);
        }

        // calculate final price, and price to be charged (if offers are applied)
        for (let _cartoffer of _offers){
            _cartoffer.chargeprice = 0;
            _cartoffer.refundprice = 0;

            for(let _offerproduct of _cartoffer.products){
                _cartoffer.chargeprice += _offerproduct.chargeprice;
                _cartoffer.refundprice += _offerproduct.refundprice;
            }

            _cartoffer.chargeprice = Math.round(_cartoffer.chargeprice * 100) / 100;
            _cartoffer.refundprice = Math.round(_cartoffer.refundprice * 100) / 100;
        }
        
        return _offers;
    }
}

/****************************************/
/* TICKET ITEMS                         */
/****************************************/

export interface CartItem {
    product?: CartProduct;
    offer?: CartOffer;
};

class CartItems {
    constructor(private _cartitems : Array <CartItem>){
        if (!_cartitems){
            this._cartitems = [];
        }
    }

    Merge(_cartitems: Array <CartItem>){
        for(let _idx = this._cartitems.length - 1; _idx >= 0; _idx--){
            if (this._indexOf(this._cartitems[_idx], _cartitems) == -1){
                this._delete(this._cartitems[_idx]);
            }
        }
        
        for(let _item of _cartitems){
            let _idx = this._indexOf(_item, this._cartitems);
            if (_idx == -1) { // insert ordered
                this._insert(_item);
            }
            else {  // update
                this._update(_item);
            }
        }

        // order the resulting list
        this._cartitems.sort((a, b) => { 
            return this._compare(a, b);
        });  

        return this._cartitems;
    }

    private _delete(_item){
        let _idx = this._indexOf(_item, this._cartitems);
        if (_idx != -1){
            this._cartitems.splice(_idx, 1);
        }
    }
    
    private _insert(_item){
        let _idx = 0;
        while((_idx < this._cartitems.length) && (this._compare(_item, this._cartitems[_idx]) > 0)){
            _idx++;
        }
        this._cartitems.splice(_idx, 0, _item);
    }
    
    private _update(_item){
        let _idx = this._indexOf(_item, this._cartitems);
        if (_idx != -1){
            let _toupdate = this._cartitems[_idx];

            if (_toupdate.product){
                _toupdate.product.requested = _item.product.requested;
                _toupdate.product.totalprice = _item.product.totalprice;
                _toupdate.product.offerprice = _item.product.offerprice;
            }

            if (_toupdate.offer){
                _toupdate.offer.requested = _item.offer.requested;
                _toupdate.offer.products = _item.offer.products;
                _toupdate.offer.chargeprice = _item.offer.chargeprice;
                _toupdate.offer.refundprice = _item.offer.refundprice;
            }
        }
    }

    private _compare(a, b){
        let _res = 0;
        
        if (!!a.product != !!b.product){
            return (a.product) ? -1 : 1;    // products before offers
        }
        
        if (_res == 0){   // order by creation date desc
            let _oa = (a.product) ? a.product.requested[0].created.getTime() : a.offer.requested[a.offer.requested.length - 1].created.getTime();
            let _ob = (b.product) ? b.product.requested[0].created.getTime() : b.offer.requested[b.offer.requested.length - 1].created.getTime();
            
            _res = (_oa == _ob) ? 0 : (_oa > _ob) ? -1 : 1;
        }
        
        return _res;
    }
    
    private _equal(o1, o2){
        if (!o1 || !o2){
            return false;       // not same type of item
        }

        for(let _property in o1) {
            if (_property == 'requested'){
                continue;   // exclude this properties from comparison
            }

            if ((o1[_property] == o2[_property])){
                continue;
            }

            if (o1[_property] instanceof Array){
                let a1 = o1[_property];
                let a2 = o2[_property];

                if (a1.length == a2.length){
                    let _every = a1.every((a1it) => {       // for each element in a1 array
                        let _some = a2.some((a2it) => {     // at least one element in a2 equals a1 item;
                            return this._equal(a1it, a2it);    
                        });
                        return _some; 
                    });

                    if (_every){
                        continue;
                    }
                }
            }            

            return false;
        }
        
        return true;
    }

    private _indexOf(needle, haystack){
        for(let _idx = 0; _idx < haystack.length; _idx++){
            if (needle.product){
                if ((haystack[_idx].product) && (this._equal(needle.product, haystack[_idx].product))){
                    return _idx;
                }    
            }

            if (needle.offer){
                if ((haystack[_idx].offer) && (this._equal(needle.offer, haystack[_idx].offer))){
                    return _idx;
                }    
            }
        }
        return -1;
    }
}

class TicketItems {
    private _cartitems: Array<CartItem> = [];
    
    get Items(){    // all cart items
        return this._cartitems;
    }
    
    private _cartproducts: CartProducts = null;    
    get CartProducts(){
        return this._cartproducts;
    }

    private _cartoffers: CartOffers = null;
    get CartOffers(){
        return this._cartoffers;
    }

    constructor(){
        this._cartproducts = new CartProducts();
        this._cartoffers = new CartOffers();
    }
    
    OnRefresh(_products, _offers){
        let _cartitems: Array<CartItem> = [];
        
        // add products
        this._cartproducts.OnRefresh(_products);
        for(let _item of this._cartproducts.Products){
            _cartitems.push({
                product: _item
            });
        }

        // add offers
        this._cartoffers.OnRefresh(_offers);
        for(let _item of this._cartoffers.Offers){
            _cartitems.push({
                offer: _item
            });
        }
        
        this._cartitems = new CartItems(this._cartitems).Merge(_cartitems);
    }
}

/*******************************/
/* TICKET UPDATES              */
/*******************************/

class TicketUpdate {
    constructor(private _ticket: Ticket, private data: dataService){
        // nothing to do
    }

    OnDestroy(){
        // nothing to do
    }

    private _onupdate = false;  // avoid reentrancy when ticket is updated

    OnUpdate(force = false){
        if (this._onupdate && !force){
            return false;   // up to date
        }

        this._onupdate = true;
        // Microtask (not setTimeout(0)): the HTML5 event-loop spec drains
        // every microtask BEFORE picking the next macrotask, so this guard
        // is guaranteed to be released before the next click event fires.
        // setTimeout(0) is a macrotask and competes FIFO with input tasks,
        // letting a quick second tap re-enter OnUpdate while _onupdate is
        // still true → the second click's recompute no-ops and ticket.price
        // stays at the first product. Microtasks are deterministic across
        // devices and don't depend on scheduler priorities. Inside a single
        // synchronous task (CartClear/CartSort loops) the microtask hasn't
        // run yet, so the cascading DoUpdate calls still batch into one
        // recompute — preserving the existing behaviour.
        queueMicrotask(() => {
            this._onupdate = false;
        });

        if (this._ticket.CopyOf){
            let _paySource = this._ticket.CopyOf as Ticket;
            if (_paySource.IsPaid && _paySource.price != null){
                return true;
            }
        }

        // On a closed ticket without local pending mutations, skip the entire
        // recompute. _ApplyOffers/_ApplyExtras and the discount loop reassign
        // child charge/refund fields, which marks them as ToUpdate even when
        // the user changed nothing — and the next DoCommit would push that
        // garbage back to the server. The check has to happen here, before
        // any of those side-effects run, so a stale cache cannot synthesise
        // pending changes that disable its own protection.
        // El short-circuit congela el precio del pagado para no pisarlo con un
        // recompute espurio (sin cambios locales). PERO hay estados de un
        // pagado que SI deben recalcular: una DEVOLUCION (el precio baja) o un
        // REABRIR (se esta editando). Sin esta excepcion el recompute correcto
        // (p.ej. 57.4 -> 43.6 al devolver) se descarta: la devolucion "no se
        // descuenta" y no se genera la rectificacion fiscal. _needsPaidRecompute
        // detecta esos casos y deja correr el recompute completo.
        if (this._ticket.IsPaid && !this._hasPendingChildChanges() && !this._needsPaidRecompute()){
            this._HandlePaidShortCircuit();
            return true;
        }

        if (this._ticket.place){
            this._ApplyOffers(this._ticket.place.offers);
            this._ApplyExtras(this._ticket.place.extras);
        }

        let _charge = 0;     // calculate the ticket charge price
        let _refund = 0;     // calculate the ticekt refund price   

        for(let _product of this._ticket.UniqueProducts){
            let _prcharge = Number(_product.ChargePrice)
            if (!_product.offer || !_product.offer.IsValid){
                _charge += _prcharge;
            }

            if (_product.status == 'UN'){
                _refund += _prcharge;
            }
        }

        for(let _offer of this._ticket.offers){
            let _ofcharge = Number(_offer.charge) 
            if (_offer.IsValid){
                _charge += _ofcharge;
            }

            if (_offer.status == 'UN'){
                _refund += _ofcharge;
            }
        }

        for(let _extra of this._ticket.extras){
            let _xtcharge =  Number(_extra.charge);
            if (_extra.IsValid){
                _charge += _xtcharge;
            }

            if (_extra.status == 'UN'){
                _refund += _xtcharge;
            }
        }

        this._ticket._charge = _charge;   // store the current charge in the ticket (pending charges)
        this._ticket._refund = _refund;   // store the current refund in the ticket (pending refunds)

        for(let _discount of this._ticket.discounts){
            _discount.charge = Math.min(_charge, _discount.ChargePrice);
            _discount.percnt = Math.round((_discount.charge / this._ticket._charge) * 100);
            _discount.refund = Math.min(_refund, _discount.RefundPrice);

            if (_discount.IsValid){
                _charge -= _discount.charge;
            }
        }

        this._ticket.price = Math.round(_charge * 100) / 100;

        _refund = 0;    // calculate ticket refund price
        if (this._ticket.IsPaid){
            for(let _product of this._ticket.UniqueProducts){
                if (!_product.offer || !_product.offer.IsValid){
                    _refund += Number(_product.RefundPrice);
                }
            }

            for(let _offer of this._ticket.offers){
                if (_offer.IsValid){
                    _refund += Number(_offer.refund);
                }
            }

            for(let _extra of this._ticket.extras){
                if (_extra.IsValid){
                    _refund += Number(_extra.refund);
                }
            }

            this._ticket._refund = _refund;   // store the current refund in the ticket (applied refunds)

            for(let _discount of this._ticket.discounts){
                if (_discount.IsValid){
                    _refund -= Number(_discount.RefundPrice);
                }
            }
        }

        this._ticket.refund = Math.round(_refund * 100) / 100;

        return true;    // has been updated
    }

    private _hasPendingChildChanges(): boolean {
        const _lists = [
            this._ticket.products,
            this._ticket.offers,
            this._ticket.extras,
            this._ticket.discounts
        ];
        for (const _list of _lists) {
            for (const _item of _list) {
                if (_item.ToInsert || _item.ToUpdate) {
                    return true;
                }
            }
        }
        return false;
    }

    // Estados de un ticket PAGADO en los que el precio SI debe recalcularse
    // (no aplicar el short-circuit que lo congela):
    //  - DEVOLUCION: lineas pendientes de reembolso ('UN') o ya aplicadas
    //    (RefundPrice/refund > 0 = 'DE' + evento de pago). OJO: cuando el
    //    short-circuit dispara, la devolucion ya suele estar en 'DE', por eso
    //    NO basta con mirar 'UN' — hay que detectar tambien el refund aplicado.
    //  - REABRIR: el ticket sigue "pagado" por el flag TicketPaid pero volvio
    //    a un status != 'PD' (reabierto y editandose). Excluimos el pago a
    //    cuenta (account != null), que es un pagado legitimo no reabierto.
    private _needsPaidRecompute(): boolean {
        if (this._ticket.status != 'PD' && this._ticket.account == null){
            return true;    // reabierto (pagado por flag, ya no en 'PD')
        }

        for (const _product of this._ticket.products){
            if (_product.status == 'UN' || Number(_product.RefundPrice) > 0){
                return true;
            }
        }
        for (const _offer of this._ticket.offers){
            if (_offer.status == 'UN' || Number(_offer.refund) > 0){
                return true;
            }
        }
        for (const _extra of this._ticket.extras){
            if (_extra.status == 'UN' || Number(_extra.refund) > 0){
                return true;
            }
        }
        return false;
    }

    // Aggregate read of children — gross charge before discounts, pending
    // refund (status='UN' items) and applied refund on paid tickets. Pure
    // function: no assignments to children or to the ticket. Used by both
    // the paid short-circuit and (could be reused by) the main recompute to
    // keep the same arithmetic in one place.
    private _ReadAggregates(): { charge: number, refund: number, paidRefund: number } {
        let _charge = 0;
        let _refund = 0;

        for (const _product of this._ticket.UniqueProducts){
            const _prcharge = Number(_product.ChargePrice);
            if (!_product.offer || !_product.offer.IsValid){
                _charge += _prcharge;
            }
            if (_product.status == 'UN'){
                _refund += _prcharge;
            }
        }
        for (const _offer of this._ticket.offers){
            if (_offer.IsValid){
                _charge += Number(_offer.charge);
            }
            if (_offer.status == 'UN'){
                _refund += Number(_offer.charge);
            }
        }
        for (const _extra of this._ticket.extras){
            if (_extra.IsValid){
                _charge += Number(_extra.charge);
            }
            if (_extra.status == 'UN'){
                _refund += Number(_extra.charge);
            }
        }

        let _paidRefund = 0;
        if (this._ticket.IsPaid){
            for (const _product of this._ticket.UniqueProducts){
                if (!_product.offer || !_product.offer.IsValid){
                    _paidRefund += Number(_product.RefundPrice);
                }
            }
            for (const _offer of this._ticket.offers){
                if (_offer.IsValid){
                    _paidRefund += Number(_offer.refund);
                }
            }
            for (const _extra of this._ticket.extras){
                if (_extra.IsValid){
                    _paidRefund += Number(_extra.refund);
                }
            }
        }

        return { charge: _charge, refund: _refund, paidRefund: _paidRefund };
    }

    // Single handler for the paid short-circuit branch. Mirrors OnUpdate's
    // sequential discount application (Math.min on a running charge) but
    // never writes to persisted child or ticket fields. Updates only:
    //   - _ticket._charge / _ticket._refund (in-memory cache)
    //   - _discount.percnt / _discount.refund (private setters, no patchValue)
    // and emits a warning when the would-be ticket price diverges from the
    // persisted one.
    private _HandlePaidShortCircuit(): void {
        const _agg = this._ReadAggregates();

        this._ticket._charge = _agg.charge;
        this._ticket._refund = this._ticket.IsPaid ? _agg.paidRefund : _agg.refund;

        let _runningCharge = _agg.charge;
        for (const _discount of this._ticket.discounts){
            const _dcharge = Math.min(_runningCharge, Number(_discount.ChargePrice));
            if (_agg.charge > 0){
                _discount.percnt = Math.round((_dcharge / _agg.charge) * 100);
            }
            _discount.refund = Math.min(_agg.refund, Number(_discount.RefundPrice));

            if (_discount.IsValid){
                _runningCharge -= _dcharge;
            }
        }

        const _recalculated = Math.round(_runningCharge * 100) / 100;
        const _stored = this._ticket.price ?? 0;
        if (Math.abs(_recalculated - _stored) > 0.005){
            console.warn('[TICKET] OnUpdate kept stored price on paid ticket — recompute diverges from DB', {
                objid: this._ticket.objid,
                stored: _stored,
                recalculated: _recalculated
            });
        }
    }

    private _AddOffer(offer: Offer, products: Array <TicketProduct>, status: string = 'AC'){
        // create the ticketoffer instance
        let _ticketoffer = new TicketOffer(null, this.data)
        if (_ticketoffer){
            _ticketoffer.ticket = this._ticket;
            _ticketoffer.offer = offer;
            _ticketoffer.status = status;
            _ticketoffer.charge = 0;
            _ticketoffer.refund = 0;
        }

        // add the offer to the ticket products 
        for(let _ticketproduct of products){
            _ticketproduct.offer = _ticketoffer;
        }

        // recalculate charge price for ticket products
        for(let _ticketproduct of products){
            if (!_ticketproduct.fixed){
                _ticketproduct.charge = _ticketproduct.ChargePrice;
            }
        }

        // recalculate charge and refund prices for ticket offers
        _ticketoffer.charge = _ticketoffer.ChargePrice;
        _ticketoffer.refund = _ticketoffer.RefundPrice;

        // add the ticket offer to the ticket
        this._ticket.AddOffer(_ticketoffer);
    }

    private _SetOffer(offer: Offer, products: Array <TicketProduct>, applied: Array <TicketOffer>){
        for(let _idx = applied.length - 1; _idx >= 0; _idx--) {
            let _ticketoffer = applied[_idx];                

            if ((_ticketoffer.IsValid) && (_ticketoffer.offer == offer) && offer.Check(this._ticket.products)){
                applied.splice(_idx, 1);

                // remove the old offer from the ticket products
                for(let _ticketproduct of this._ticket.products){
                    if (_ticketproduct.offer == _ticketoffer){
                        _ticketproduct.offer = null;
                    }
                }

                // reuse the ticket offer with the new products list
                for(let _ticketproduct of products){
                    _ticketproduct.offer = _ticketoffer;
                }

                // recalculate charge price for ticket products
                for(let _ticketproduct of this._ticket.products){
                    if (!_ticketproduct.fixed){
                        _ticketproduct.charge = _ticketproduct.ChargePrice;
                    }        
                }

                // recalculate charge and refund prices for ticket offers
                for(let _ticketoffer of this._ticket.offers){
                    _ticketoffer.charge = _ticketoffer.ChargePrice;
                    _ticketoffer.refund = _ticketoffer.RefundPrice;
                }

                return true;
            }
        }

        return false;
    }

    private _SetExtra(extra: Extra, applied: Array <TicketExtra>){
        for(let _idx = applied.length - 1; _idx >= 0; _idx--){
            let _ticketextra = applied[_idx];
            if ((_ticketextra.IsValid) && (_ticketextra.extra == extra)){
                applied.splice(_idx, 1);

                // reuse the ticket extra with the current price
                _ticketextra.charge = _ticketextra.ChargePrice;
                _ticketextra.refund = _ticketextra.RefundPrice;
                
                return true;
            }
        }

        return false;
    }

    private _AddExtra(extra: Extra){
        // create the ticketextra instance
        let _ticketextra = new TicketExtra(null, this.data)
        if (_ticketextra){
            _ticketextra.extra = extra;
            _ticketextra.ticket = this._ticket;
            _ticketextra.status = 'AC';
            _ticketextra.charge = 0;
            _ticketextra.refund = 0;
        }

        _ticketextra.charge = _ticketextra.ChargePrice;
        _ticketextra.refund = _ticketextra.RefundPrice;

        // add the ticket extra to the ticket
        this._ticket.AddExtra(_ticketextra);
    }

    private _ApplyOffers(_offers: Array <Offer>){
        if (this._ticket.isopen){
            // Lista candidata para el MATCHING de ofertas: deduplicada. Offer.Check
            // asigna una fila por grupo, asi que dos instancias transitorias de la
            // MISMA linea (copia optimista + persistida) podrian satisfacer una
            // oferta de varios grupos del mismo producto; luego UniqueProducts
            // quita una, pero la oferta ya quedo aplicada -> precio incorrecto.
            // La persistencia de TODAS las filas se hace por otras vias (products).
            let _ticketproducts = [];
            for(let _product of this._ticket.UniqueProducts){
                if (_product.IsValid && !_product.Separator){
                    _ticketproducts.push(_product);
                }
            }
    
            let _toapplyoffers = [];
    
            // get the list of currently active offers
            let _active: Array<Offer> = []; 
            for(let _offer of _offers){
                if (_offer.IsActive){
                    _active.push(_offer);
                }
            }
    
            if (_active.length > 0){
                // get all offers that can apply to the ticket products
                let _candidates = [];
                do {
                    _candidates = [];
                    for(let _candidate of _active){
                        let _applyinfo = { applied: [], savings: 0};
                        if (_candidate.Check(_ticketproducts, _applyinfo)){
                            _candidates.push({
                                offer: _candidate,
                                status: 'AC',
                                applied: _applyinfo.applied,
                                savings: _applyinfo.savings
                            });
                        }
                    }
    
                    // apply the best candidate to the ticket products
                    if (_candidates.length > 0){
                        _candidates = _candidates.sort((a, b) => {
                            if (b.savings == a.savings){
                                return (b.offer.price - a.offer.price);
                            }
                            return b.savings - a.savings;
                        });
    
                        let _best = _candidates[0];
    
                        // remove the applied products from the products list
                        for(let _ticketproduct of _best.applied){
                            let _idx = _ticketproducts.indexOf(_ticketproduct);                    
                            if (_idx != -1){
                                _ticketproducts.splice(_idx, 1);
                            }
                        }
    
                        _toapplyoffers.push(_best);
                    }
                } while(_candidates.length > 0);
            }
    
            // remove the previous offer information
            for(let _ticketproduct of this._ticket.products){
                _ticketproduct.offer = null;
                
                if (!_ticketproduct.fixed){
                    _ticketproduct.charge = _ticketproduct.ChargePrice;
                }                  
            }
    
            // merge the applying offers to the current offers
            let _appliedoffers = this._ticket.offers.slice(0);
            for(let _applyoffer of _toapplyoffers){
                if (!this._SetOffer(_applyoffer.offer, _applyoffer.applied, _appliedoffers)){
                    this._AddOffer(_applyoffer.offer, _applyoffer.applied, 'AC');
                }
            }
    
            // remove the non-reused applied offers from the ticket
            for(let _ticketoffer of _appliedoffers){
                _ticketoffer.status = 'DE';
            }    
        }

        // recalculate offers charge and refund prices
        for(let _ticketoffer of this._ticket.offers){
            _ticketoffer.charge = _ticketoffer.ChargePrice;
            _ticketoffer.refund = _ticketoffer.RefundPrice;
        }  
    }

    private _ApplyExtras(_extras: Array <Extra>){
        if (this._ticket.isopen){
            // get the list of currently applying extras
            let _toapplyextras: Array<Extra> = []; 
            for(let _extra of _extras){
                if (_extra.IsActive && _extra.ApplyToTicket(this._ticket)){
                    _toapplyextras.push(_extra);
                }
            }

            // merge the applying extras to the current extras
            let _appliedextras = this._ticket.extras.slice(0);
            for(let _extra of _toapplyextras){
                if (!this._SetExtra(_extra, _appliedextras)){
                    this._AddExtra(_extra);
                }
            }

            // remove the non-reused applied extras from the ticket
            for(let _ticketextra of _appliedextras){
                _ticketextra.status = 'DE';
            }
        }

        // recalculate extras charge and refund prices
        for(let _ticketextra of this._ticket.extras){
            _ticketextra.charge = _ticketextra.ChargePrice;
            _ticketextra.refund = _ticketextra.RefundPrice;        
        }
    }
}

/*******************************/
/* TICKET CONTROL              */
/*******************************/

class TicketControl {
    private _ticket_update: TicketUpdate = null;

    private _refresh_subscription = null;
    private _update_subscription = null;

    constructor(private _ticket: Ticket, private data: dataService){
        this._ticket_update = new TicketUpdate(this._ticket, this.data);

        this._update_subscription = this._ticket.OnModify.subscribe(
        data => {
            this._updateticketitems = true;
        });
    }

    private _items: TicketItems = null;
    private get Items(){
        if (this._items == null){
            this._items = new TicketItems();

            if (this._refresh_subscription){
                this._refresh_subscription.unsubscribe();
                this._refresh_subscription = null;
            }

            this.OnUpdate(true);
            this._refresh_subscription = this._ticket.OnRefresh.subscribe(
            data => {
                this.OnUpdate();
            });            
        }

        return this._items.Items;
    }

    DoUpdate(force = false){
        this.OnUpdate(force);
    }

    private _updateticketitems: boolean = true; 
    private _updateprtoprepare: boolean = true;
    private _updateprtorefund: boolean = true;
    private _updateprdelivered: boolean = true;
    private _updateprcancelled: boolean = true;
    private _updateprtoseparate: boolean = true;
    private _updateoftorefund: boolean = true;
    private _updateoftodeliver: boolean = true;
    private _updatexttoprepare: boolean = true;
    private _updatexttorefund: boolean = true;
    private _updatextodeliver: boolean = true;

    public _onProductsUpdated = new Subject<any>();
    private OnUpdate(force = false){
        if (!this._updateticketitems && !force){
            return;     // ticket is up to date  
        }

        if (this._ticket_update.OnUpdate(force)){
            if (this.Items){
                let _prev = this._items.CartProducts.Products.length;
                this._items.OnRefresh(this._ticket.products, this._ticket.offers);
                let _next = this._items.CartProducts.Products.length;

                if (_prev != _next){
                    setTimeout(() => {
                        this._onProductsUpdated.next();
                    }, 50);
                }
            }
    
            this._updateprtoprepare = true;
            this._updateprtorefund = true;
            this._updateprdelivered = true;
            this._updateprcancelled = true;
            this._updateprtoseparate = true;
            this._updateoftorefund = true;
            this._updateoftodeliver = true;
            this._updatexttoprepare = true;
            this._updatexttorefund = true;
            this._updatextodeliver = true;
            this._updateticketitems = false;    
        }
    }

    OnDestroy(){
        if (this._ticket_update){
            this._ticket_update.OnDestroy();
            this._ticket_update = null;
        }

        if (this._update_subscription){
            this._update_subscription.unsubscribe();
            this._update_subscription = null;
        }

        if (this._refresh_subscription){
            this._refresh_subscription.unsubscribe();
        }
        this._refresh_subscription = null;
    }

    private _productstoprepare = [];
    get ProductsToPrepare() {
        if (this._updateprtoprepare){
            this._updateprtoprepare = false;

            let _products = [];
            for(let _item of this.Items){
                if (_item.product){
                    if (['UN', 'DE', 'SP'].includes(_item.product.requested[0].status)){
                        continue;
                    }

                    let _ispd = _item.product.requested[0].HasEvent('PD');
                    let _isrd = _item.product.requested[0].HasEvent('RD');
                    if (this._ticket.IsDirect){
                        if (!_ispd){
                            _products.push(_item);
                        }
                    }
                    else {
                        if (!_isrd) {
                            _products.push(_item);
                        }    
                    }
                }
            }

            this._productstoprepare = new CartItems(this._productstoprepare).Merge(_products);
            this._productstoprepare.sort((a, b) => {
                return a.product.sort - b.product.sort;
            });
        }

        return this._productstoprepare;
    }

    private _productstoseparate = [];
    get ProductsToSeparate(){
        if (this._updateprtoseparate){
            this._updateprtoseparate = false;

            let _products= [];
            for(let _item of this.Items){
                if (_item.product){
                    if (['UN', 'DE'].includes(_item.product.requested[0].status)){
                        continue;
                    }

                    let _ispd = _item.product.requested[0].HasEvent('PD');
                    let _isrd = _item.product.requested[0].HasEvent('RD');
                    if (this._ticket.IsDirect){
                        if (!_ispd){
                            _products.push(_item);
                        }
                    }
                    else {
                        if (!_isrd) {
                            _products.push(_item);
                        }    
                    }
                }
            }

            let _productstoseparate = new CartItems(this._productstoseparate).Merge(_products);
            _productstoseparate.sort((a, b) => {
                return a.product.sort - b.product.sort;
            });

            this._productstoseparate = _productstoseparate;
        }

        return this._productstoseparate;        
    }

    private _productstorefund = [];
    get ProductsToRefund() { 
        if (this._updateprtorefund){
            this._updateprtorefund = false;

            let _products = [];
            for (let _item of this.Items){
                if (_item.product && ['SP', 'UN'].includes(_item.product.requested[0].status)){
                    _products.push(_item);
                }
            }

            this._productstorefund = new CartItems(this._productstorefund).Merge(_products);
            this._productstorefund.sort((a, b) => {
                return a.product.sort - b.product.sort;
            });
        }

        return this._productstorefund;
    }

    private _productsdelivered = [];
    get ProductsDelivered() {
        if (this._updateprdelivered){
            this._updateprdelivered = false;

            let _products = [];
            for(let _item of this.Items){
                if (_item.product){
                    if (['UN', 'DE', 'SP'].includes(_item.product.requested[0].status)){
                        continue;
                    }

                    if (this._ticket.IsDirect){
                        let _ispd = _item.product.requested[0].HasEvent('PD');
                        if (_ispd){
                            _products.push(_item);
                        }
                    }
                    else {
                        let _isrd = _item.product.requested[0].HasEvent('RD');  
                        if (_isrd){
                            _products.push(_item);
                        }                        
                    }                    
                }
            }

            this._productsdelivered = new CartItems(this._productsdelivered).Merge(_products);
            this._productsdelivered.sort((a, b) => {
                return a.product.sort - b.product.sort;
            });
        }

        return this._productsdelivered;
    }

    private _productscancelled = [];
    get ProductsCancelled(){
        if (this._updateprcancelled){
            this._updateprcancelled = false;

            let _products= [];
            for(let _item of this.Items){
                if (_item.product && ['DE', 'SP'].includes(_item.product.requested[0].status)){
                    let _iscc = _item.product.requested[0].HasEvent('CC');
                    if (_iscc){
                        _products.push(_item);
                    }
                }
            }

            this._productscancelled = new CartItems(this._productscancelled).Merge(_products);
            this._productscancelled.sort((a, b) => {
                return a.product.sort - b.product.sort;
            });
        }

        return this._productscancelled;        
    }

    private _offerstorefund = [];
    get OffersToRefund() {
        if (this._updateoftorefund){
            this._updateoftorefund = false;

            let _offers = [];
            for (let _item of this.Items){
                if (_item.offer){
                    let _torefund = _item.offer.products.some(
                    (_product) => {
                        return (_product.refund.length > 0);
                    });

                    if (_torefund){
                        _offers.push(_item);
                    }
                }
            }

            this._offerstorefund = new CartItems(this._offerstorefund).Merge(_offers);
        }
        
        return this._offerstorefund;    
    }

    private _offerstodeliver = [];
    get OffersToDeliver() {
        if (this._updateoftodeliver){
            this._updateoftodeliver = false;

            let _offers = [];
            for (let _item of this.Items){
                if (_item.offer){
                    let _todeliver = _item.offer.products.some(
                    (_product) => {
                        return (_product.active.length > 0);
                    });

                    if (_todeliver){
                        _offers.push(_item);
                    }
                }
            }
            this._offerstodeliver = new CartItems(this._offerstodeliver).Merge(_offers);
        }
        
        return this._offerstodeliver;    
    }

    private _extrastoprepare = [];
    get ExtrasToPrepare() {
        if(this._updatexttoprepare){
            this._updatexttoprepare = false;

            let _extras = [];
            for (let _extra of this._ticket.extras){
                if ((_extra.status == 'AC') && (_extra.charge > 0)){
                    _extras.push(_extra);
                }
            }
            this._extrastoprepare = _extras;
        }

        return this._extrastoprepare;
    }

    private _extrastorefund = [];
    get ExtrasToRefund() {
        if (this._updatexttorefund){
            this._updatexttorefund = false;

            let _extras = [];
            for (let _extra of this._ticket.extras){
                if ((['AC', 'UN'].includes(_extra.status)) && (_extra.refund > 0)){
                    _extras.push(_extra);
                }
            }
            this._extrastorefund = _extras;
        }
        
        return this._extrastorefund;    
    }

    private _extrastodeliver = [];
    get ExtrasToDeliver() {
        if (this._updatextodeliver){
            this._updatextodeliver = false;

            let _extras = [];
            for (let _extra of this._ticket.extras){
                if (['AC', 'UN'].includes(_extra.status)){
                    _extras.push(_extra);
                }
            }
            this._extrastodeliver = _extras;
        }
        
        return this._extrastodeliver;    
    }

    get DiscountsToApply(){
        let _discounts = [];

        for (let _discount of this._ticket.discounts){
            if (_discount.IsValid){
                _discounts.push(_discount);
            }
        }

        return _discounts;
    }

    get DiscountsToRefund(){
        let _discounts = [];

        for (let _discount of this._ticket.discounts){
            if (_discount.IsValid && (_discount.refund > 0)){
                _discounts.push(_discount);
            }
        }

        return _discounts;
    }

    get ToPrepare(){
        let _result = this._ticket.IsValid;

        if (_result){   // if direct ticket do not prepare (only payment)
            _result = !this._ticket.IsDirect;
        }

        if (_result){   // if prepay ticket, ticket must have been paid
            if (this._ticket.IsPrepay){
                _result = this._ticket.IsPaid;
            }
        }

        if (_result){   // it is to prepare if it is not ready
            _result = !this._ticket.IsReady;
        }

        return _result;
    }

    get ToPayment(){
        return this._ticket.IsValid && !this._ticket.IsPaid;
    }

    get refund(){
        if (!this._ticket.IsPaid){
            return 0;
        }

        let _refund: number = 0;

        for(let _ticketproduct of this._ticket.UniqueProducts){
            if ((_ticketproduct.status == 'UN') && (!_ticketproduct.offer)) {
                _refund += _ticketproduct.charge;
            }
        }

        for(let _ticketoffer of this._ticket.offers){
            if (_ticketoffer.IsValid){
                _refund += _ticketoffer.refund;
            }
        }

        for(let _ticketextra of this._ticket.extras){
            if (_ticketextra.IsValid){
                _refund += _ticketextra.refund;
            }
        }

        for (let _ticketdiscount of this._ticket.discounts){
            if (_ticketdiscount.IsValid){
                _refund -= _ticketdiscount.refund;
            }
        }

        return Math.abs(Math.round(_refund * 100) / 100);
    }

    get ToRefund(){
        let _refund = this.refund;
        if (_refund > 0){
            return _refund.toFixed(2);
        }

        return false;
    }

    ItemSort(cartproduct, delta, cartitems){
        let _cartproducts = new CartProducts(cartitems);
        if (_cartproducts){
            _cartproducts.MoveCartProduct(cartproduct, delta);
        }

        if (this._items){
            this._items.OnRefresh(this._ticket.products, this._ticket.offers);
        }

        this._updateprtoprepare = true;
        this._updateprtoseparate = true;      
        this._updateprtorefund = true;
        this._updateprdelivered = true;
        this._updateprcancelled = true;  
    }
}

export class Ticket extends TicketData {
    // report-only metadata: not persisted on the TICKET table; populated by report endpoints (e.g. place/ticketlst.php)
    public hasdiscount?: boolean;

    constructor(objid: string, data: dataService, objoptions: ObjectOptions = null){
        super('TICKET', objid, data, objoptions);
    }

    Copy(store: Array<DataObject> = []) : Ticket {
        return this._Copy(store) as Ticket;
    }

    protected OnDestroy(){
        if (this._ticketcontrol){
            this._ticketcontrol.OnDestroy();
            this._ticketcontrol = null;
        }
    }

    private _ticketcontrol: TicketControl = null;
    get TicketControl(){
        if (this._ticketcontrol == null){
            this._ticketcontrol = new TicketControl(this, this.data);
        }
        
        return this._ticketcontrol;
    }

    get OnProducts(){
        return this.TicketControl._onProductsUpdated.asObservable();
    }

    /****************************/
    /* BASE OVERLOAD           */
    /****************************/

    protected get _CanUpdate(){
        // No pisar con refresh stale mientras hay commit/sync en vuelo al server.
        // No usar ToInsert/ToUpdate del borrador — eso es el estado normal al editar.
        return !this._hasServerSyncInFlight();
    }

    private _lastproducts = new Map <TicketProduct, string> ();

    protected _OnUpdate(info){
        super._OnUpdate(info);

        let _update = (this._lastproducts == null);
        if (!_update){
            _update = this._lastproducts.size != this.products.length;
        }

        if (!_update){
            _update = !this.products.every(
            (_ticketproduct) => {
                let _lastproducthash = this._lastproducts.get(_ticketproduct);
                if (_lastproducthash){
                    return (_ticketproduct.hash == _lastproducthash);
                }
                return false;   // new ticketproduct
            });
        }

        if (_update){
            this.DoChange("set", null);     // trigger the ticket change

            // update the last products list
            this._lastproducts.clear();
            for (let _ticketproduct of this.products){
                this._lastproducts.set(_ticketproduct, _ticketproduct.hash);
            }
        }
    }

    /****************************/
    /* TICKET PRINTING          */
    /****************************/

    private _onPrint = new Subject<any>();
    public OnPrint = this._onPrint.asObservable();
    private _onEmail = new Subject<any>();
    public OnEmail = this._onEmail.asObservable();
    private _onDrawer = new Subject<any>();
    public OnDrawer = this._onDrawer.asObservable();

    private _onModify = new Subject<any>();
    public OnModify = this._onModify.asObservable();
    private _onCancel = new Subject<any>();
    public OnCancel = this._onCancel.asObservable();
    private _onCommit = new Subject<any>();
    public OnCommit = this._onCommit.asObservable();

    private _print_requested = false;   // printing has been requested
    private _dopen_requested = false;   // drawer opening has been requested
    private _onpay_requested = false;   // print on ticket pay requested

    // Public read-only para que los flujos de cierre del editor (race
    // entre _onPrint emit vs DoCommit completo) pregunten si va a haber
    // emit sin tocar campo privado.
    get PrintRequested(): boolean {
        return this._print_requested;
    }

    ToPrinter(_printer){
        if (!_printer){
            console.warn("[TICKET] No printer has been provided");
        }
        else {
            this._onPrint.next({
                printer: _printer,
                isauto: false,
                drawer: false,
                claim: _makePrintClaim()
            });
        }
    }

    ToPaytefPrinter(_terminal){
        if (!_terminal){
            console.warn("[TICKET] No payment terminal has been provided");
        }
        else {
            this._onPrint.next({
                terminal: _terminal,
                isauto: false,
                drawer: false,
                claim: _makePrintClaim()
            });
        }
    }

    // Paytef auto-print stash + fallback por uuid (sync puede swappear la instancia mid-paytef).
    private _pendingPaytefTerminal: any = null;
    private static _pendingPaytefByUuid: Map<string, any> = new Map();

    private static readonly _PAYTEF_UUID_MAP_CAP = 32;
    private static readonly _PAYTEF_UUID_MAP_TTL_MS = 60_000;

    // Exposed so the editor can read the stash on the COPY (where paytef
    // modal stashed via RequestPaytefAutoPrint) and re-stash it on the
    // ORIGINAL before DoCommit — uuids differ between COPY and ORIGINAL,
    // so the static uuid map lookup in DoCommit wouldn't find it otherwise.
    get PendingPaytefTerminal(){
        return this._pendingPaytefTerminal;
    }

    RequestPaytefAutoPrint(_terminal){
        const _uuid = (this as any)._uuid;
        this._pendingPaytefTerminal = _terminal;
        if (_uuid){
            // FIFO (eviction por orden de insercion, sin promotion en
            // acceso) + TTL para evitar crecimiento del map.
            while (Ticket._pendingPaytefByUuid.size >= Ticket._PAYTEF_UUID_MAP_CAP){
                const _oldestKey = Ticket._pendingPaytefByUuid.keys().next().value;
                Ticket._pendingPaytefByUuid.delete(_oldestKey);
                console.warn("[PAYTEF] uuid map cap reached, dropping " + _oldestKey);
            }
            Ticket._pendingPaytefByUuid.set(_uuid, _terminal);
            setTimeout(() => {
                if (Ticket._pendingPaytefByUuid.get(_uuid) === _terminal){
                    Ticket._pendingPaytefByUuid.delete(_uuid);
                }
            }, Ticket._PAYTEF_UUID_MAP_TTL_MS);
        }
    }

    DoPrint(drawer: boolean = false, isauto: boolean = false, oncommit: boolean = false){
        if (oncommit){
            this._print_requested ||= true;
            this._dopen_requested ||= drawer;
            this._onpay_requested ||= isauto;
        }
        else {
            this._onPrint.next({
                printer: null,
                isauto: isauto,
                drawer: drawer,
                claim: _makePrintClaim()
            });
        }
    }

    DoEmail(){
        this._onEmail.next();
    }

    private _printing = false;
    get printing(): boolean {
        return this._printing;
    }

    private _printCompleted = null;
    set printing(value: boolean){
        this._printing = value;
        if (value == true){
            this._printCompleted = new Subject<any>();
        }
        else {
            if (this._printCompleted){
                this._printCompleted.next();
                this._printCompleted = null;    
            }
        }
    }

    async LogTicketChange(){
        let _last = this.change;    // last ticket change
        let _reason = 'X';          // change reason ('X' for not issued)

        // determine if the ticket has been changed
        let iscancel = this.IsCancelled || this.IsEmpty;
        let ischange = false;
        if (_last == null){     // last is not provided (will be achange unless it is cancelled)
            ischange = !iscancel;
        }
        else {                  // last is provided (check against current status)
            ischange = _last.isChange;
        }

        /* NOTE: Only the paid tickets must provide a track of the changes */
        if (ischange && !this.IsPaid){
            // use 'X' for internal numeration (series and invoices)
            if (this._ticket.invceno == null){
                this._ticket.series = 'X' + this.data.serial;
                this._ticket.invceno =  await this.data.NextInvoice('X');
            }

            ischange = this.IsToPay;   // false if not paid and not to be paid
        }

        if (iscancel){
            _reason = 'C';
        }

        if (!iscancel && ischange){
            _reason = _last ? _last.Reason : 'F';
            if (this.status == 'PR'){
                _reason = 'F';  // always first ticket until ticket is commited
            }

        }

        // Comanda pagada por OTRA sesion (QR prepago: el cliente paga por
        // Stripe, el server la marca PD y los dispositivos la reciben por
        // sync YA pagada). El PRIMER cambio fiscal ('F') lo emite UN SOLO
        // dispositivo: el dueño (IsPaidProcessOwner). El QR no puede: tras
        // Stripe el carrito ya no monta ui-change-print para tickets PD.
        // Respaldo: pasada la gracia sin TICKETCHANGE, otro dispositivo puede
        // emitir. No afecta al pago local ni a cancelaciones/rectificaciones.
        if ((_reason == 'F') && this.PaidByAnotherSession &&
            !this.IsPaidProcessOwner && !this.RemotePaidGraceElapsed){
            console.info("[TICKET] Skip fiscal change ('F'): ticket paid by another session (owner device will issue it)");
            ischange = false;
        }

        console.info("[TICKET] " + this._ticket.series + " " + this._ticket.invceno + " (" + _reason + ")");

        // log the action for this device
        this.data.LogTicketAction(this);
        if (!ischange){
            return;     // not a relevant change
        }

        let _lockms = performance.now();
        let _change: any = null;
        try {
            console.info("[GLOBAL MUTEX] - waiting for mutex..")
            await _globalmutex.lock("__ticket_invoice");       // lock until last ticket is written

            // generate the serial and invoice numbers inside the mutex
            // to prevent two concurrent tickets from getting the same number
            if (_reason != null && _reason != 'X' && _reason != 'C'){
                switch(_reason){
                    case 'F':   // this is the first change
                        if (!this.invoice){
                            this._ticket.series = 'S' + this.data.serial;
                            this._ticket.invceno = await this.data.NextInvoice('T');
                        }
                        else {  // the first change is an invoice
                            this._ticket.series = 'C' + this.data.serial;
                            this._ticket.invceno = await this.data.NextInvoice('C');
                        }
                        break;

                    case 'I':   // this is an invoice creation
                        this._ticket.series = 'F' + this.data.serial;
                        this._ticket.invceno = await this.data.NextInvoice('F');
                        break;

                    default:    // this is a ticket change
                        this._ticket.series = 'R' + this.data.serial;
                        this._ticket.invceno = await this.data.NextInvoice('R');
                        break;
                }

                this._toUpdate = true;
                this.DoRefresh('TICKET', true);
            }

            // create the change entry after invoice number is finalized
            _change = new TicketChange(null, this.data);
            if (_change) {
                let _price = this.TotalTaxes;

                _change.session = this.data.session;
                _change.ticket = this;
                _change.prev = _last;
                _change.reason = _reason;
                _change.series = this._ticket.series;
                _change.invoice = this._ticket.invceno;
                _change.total = this._ticket.price;
                _change.total = _price.total;
                _change.base = _price.base;
                _change.taxes = _price.taxes;
                _change.client = this.Client;
            }

            this.AddChange(_change);

            // Firma DENTRO del mutex y ANTES de SetLastInvoice. El encadenamiento
            // TicketBAI/VeriFactu es SECUENCIAL y POR DISPOSITIVO: la cadena
            // (`last`) se guarda por (place, serial) y serial = CRC32(device+place),
            // practicamente unico por dispositivo (CRC32 admite colisiones
            // teoricas, despreciables aqui), asi que cada dispositivo tiene SU
            // propia cadena — no se coordina con otros (dos TPV llevan numeraciones
            // independientes, es normal). La huella anterior que lee _XMLHuella
            // (GetLastInvoice) debe ser la ULTIMA factura que firmo ESTE
            // dispositivo, y SetLastInvoice debe persistirla con SU firma real
            // (tbai/vftu) para que la siguiente encadene con ella. Sacar la firma
            // a background rompia la cadena: SetLastInvoice corria con
            // _change.ticketbai inexistente (tbai=null) y, al fijar `last`=esta
            // factura antes de firmarla, GetLastInvoice devolvia la PROPIA
            // (auto-referencia) -> "posible error de encadenamiento" (010) en
            // Gipuzkoa. Refresh() solo firma en local (no envia a Hacienda), asi
            // que el mutex solo se retiene el tiempo de la firma; el emit
            // OnCommit no espera esto (es fire-and-forget).
            if (_change){
                try {
                    await _change.AddVeriFactu();
                    await _change.AddTicketBAI();
                    // Validar la FIRMA que SetLastInvoice persistira como huella
                    // `last` (_change.verifactu.sign / _change.ticketbai.sign), NO
                    // el retorno de AddVeriFactu/AddTicketBAI: ese es {info,code}=
                    // id/qr y puede venir no-null con sign=null (o el child ni
                    // existe si Refresh dio null y no se hizo AddChild). Si la
                    // feature esta activada y falta la firma, abortamos: no
                    // persistir `last` con tbai/vftu=null (rompe el encadenamiento)
                    // ni emitir una factura sin firma fiscal.
                    let _fiscalref = this._ticket.series + " " + this._ticket.invceno + " (reason " + _reason + ")";
                    if (this.place.SendVFTEnabled && !_change.verifactu?.sign){
                        throw new Error("VeriFactu signing produced no signature: " + _fiscalref);
                    }
                    if (this.place.SendBAIEnabled && !_change.ticketbai?.sign){
                        throw new Error("TicketBAI signing produced no signature: " + _fiscalref);
                    }
                } catch(e) {
                    // Deshacer el AddChange de arriba: si no, queda un
                    // TicketChange SIN firma en this.changes que el siguiente
                    // commit veria como ultimo (getter `change` ordena por
                    // created) y podria persistir un TICKETCHANGE sin tbai/vftu.
                    this.DelChange(_change);
                    // NO revertimos el contador fiscal (NextInvoice ya avanzo): el
                    // numero abortado queda como hueco y el reintento usa el
                    // siguiente. Es INTENCIONAL — revertir el contador es inseguro
                    // (SetLastTicket/LastServed lo mergean con Math.max desde el
                    // server; restaurar a N-1 puede acabar reusando N = factura
                    // fiscal DUPLICADA, error 5040 AEAT, peor que un hueco). Ademas
                    // el hueco NO rompe el encadenamiento: TicketBAI/VeriFactu
                    // encadenan por huella de firma, no por correlatividad del num.
                    // Propagar: si la firma falla NO debemos ejecutar
                    // SetLastInvoice (persistiria `last` con tbai/vftu=null y
                    // rompe el encadenamiento) ni continuar el commit (dejaria
                    // una factura fiscal sin trazabilidad). El throw sale por el
                    // finally (libera el mutex) y hace rechazar LogTicketChange;
                    // DoCommit lo captura en _ticketReady y aborta antes de
                    // super.DoCommit (no se emite papel ni se persiste el ticket).
                    console.error("[TICKET] VeriFactu/TicketBAI crypto failed", e);
                    throw e;
                }
            }

            // SetLastInvoice tras firmar: guarda N con su firma para que N+1
            // encadene con ella. El contador de numeracion ya lo incremento
            // NextInvoice (GetLastTicket +=1), asi que esto solo confirma
            // (Math.max, no retrocede) y actualiza la huella `last`. Es async y
            // hay que AWAITARLO dentro del mutex: si no, el finally libera el
            // mutex antes de persistir `last` y el siguiente ticket firmaria
            // leyendo el `last` viejo (rompe el encadenamiento).
            await this.data.SetLastInvoice(_change);
        }
        finally {
            _globalmutex.unlock("__ticket_invoice");
            console.info("[GLOBAL MUTEX] - mutex is released (" + (performance.now() - _lockms).toFixed(3) + " ms)");
        }
    }

    private _ticketInCommit = null;     // serialize this ticket commit operations
    private _waitForLastCommit(){
        if (this._ticketInCommit){
            return new Promise((resolve) => {
                console.log("[TICKET] Waiting for previous commit to be completed..");

                let _subscription = this._ticketInCommit.asObservable().subscribe(
                data => {   // ticket has been commited
                    _subscription.unsubscribe();
                    console.log("[TICKET] Commit is completed. Continue.");
                    resolve(true);  
                });
            });
        }

        return false;   // no pending commits
    }

    // Motivo del ultimo DoCommit que devolvio false, para que la vista avise con el
    // texto correcto (lo lee en el .then del propio DoCommit):
    //   'fiscal'   -> fallo al preparar el cobro (alta del ticket, firma TicketBAI /
    //                 VeriFactu, LogTicketChange): no se llego a enviar NADA, y el rescate
    //                 de cobros no lo ve (no hay lote sin eco). NO se reintenta solo.
    //   'rejected' -> el lote no se encolo o el servidor no lo acepto: el ticket queda
    //                 re-marcado y el rescate lo reintenta.
    //   null       -> el ultimo DoCommit fue bien.
    private _lastCommitFailure: 'fiscal' | 'rejected' = null;
    get LastCommitFailure(): 'fiscal' | 'rejected' {
        return this._lastCommitFailure;
    }

    async DoCommit(force: boolean = true){
        let _ini = performance.now();

        // open the drawer before commiting        
        if (this._dopen_requested){
            this._dopen_requested = false;
            
            this._onDrawer.next({
                isauto: this._onpay_requested
            });

            console.info("[TICKET] On drawer check completed in " + (performance.now() - _ini).toFixed(2) + " ms")
        }

        // lock concurrent ticket commits over this ticket
        if (this._ticketInCommit){
            await this._waitForLastCommit();
        }
        this._ticketInCommit = new Subject<any>();

        console.info("[TICKET] Wait for last commit completed in " + (performance.now() - _ini).toFixed(2) + " ms")

        // pre-create ticket information — fire-and-forget para no
        // bloquear el emit OnCommit (~5s HTTP en primer ticket
        // post-recarga). Se awaita antes de super.DoCommit para que
        // el sync engine vea _toInsert=false + objid/orderno seteados
        // y persista como do_update (sin duplicar).
        let _createPromise: Promise<void>;
        if (this.ToInsert){
            _createPromise = (async () => {
                let _ticket = await this.data.CreateTicket(this);
                if (_ticket){
                    this._toInsert = false;

                    this.patchValue(this._ticket, 'objid', _ticket['objid']);
                    this.patchValue(this._ticket, 'orderno', _ticket['orderno']);

                    this._isLoaded = true;
                    this._toUpdate = true;

                    this.OnResolve();

                    console.info("[TICKET] Created ticket [" + this.objid + "] completed in " + (performance.now() - _ini).toFixed(2) + " ms");
                }
                else {
                    // Offline o red caida: seguir como do_insert (_uuid). El server
                    // asigna objid/orderno al sincronizar — comportamiento de
                    // 2.0.2.16. Abortar aqui (2.0.2.17) dejaba la comanda en la
                    // lista con spinner infinito y sin cola de sync.
                    console.warn("WARNING: Could not pre-create ticket — will commit as do_insert");
                }
            })();
            // Evitar unhandled-rejection si falla antes del await. El
            // await explicito de abajo sigue recibiendo el reject.
            _createPromise.catch(() => {});
        }
        else {
            _createPromise = Promise.resolve();
        }

        // Consume paytef stash — per-instancia primero, uuid map fallback (sync puede haber swappeado).
        const _uuid = (this as any)._uuid;
        const _paytefTerminal = this._pendingPaytefTerminal || (_uuid ? Ticket._pendingPaytefByUuid.get(_uuid) : null);
        this._pendingPaytefTerminal = null;
        if (_uuid){
            Ticket._pendingPaytefByUuid.delete(_uuid);
        }

        // ticketReady: LogTicketChange (NextInvoice + mutex) + firma
        // VeriFactu/TicketBAI. Fire-and-forget, se pasa en el payload
        // del emit y los suscriptores la awaitan en su setTimeout —
        // NextInvoice espera LastServedReady (~9s en primer ticket)
        // y sin este pattern bloqueaba el editor 16s+.
        //
        // Encadena _createPromise primero. CreateTicket solo falla en offline/red;
        // en ese caso seguimos con do_insert. LogTicketChange no corre si el
        // server devolvio error duro (CreateTicket hace toast y null).
        const _ticketReady: Promise<void> = (async () => {
            await _createPromise;
            await this.LogTicketChange();
            console.info("[TICKET] Keep ticket changes completed in " + (performance.now() - _ini).toFixed(2) + " ms")
        })();
        // Marca el reject como manejado (LogTicketChange ahora propaga el fallo
        // de firma). Si algo sincrono entre aqui y el `await _ticketReady` de
        // abajo aborta DoCommit, evita el unhandled-rejection; el await real
        // sigue observando el error. Mismo patron que _createPromise/_commitReady.
        _ticketReady.catch(() => {});

        // commitReady: resuelve cuando super.DoCommit confirma OK,
        // rechaza si falla. Los prints (ui-print, ui-change-print) la
        // awaitan en su setTimeout para no emitir papel si el backend
        // no persistio.
        let _commitResolve: () => void;
        let _commitReject: (err: any) => void;
        const _commitReady: Promise<void> = new Promise((resolve, reject) => {
            _commitResolve = resolve;
            _commitReject = reject;
        });
        // Marca el reject como manejado por si falla antes de que los
        // consumers adjunten su await. Los .catch reales siguen recibiendo.
        _commitReady.catch(() => {});

        // Paytef autoprint via overlay root-level (receipt DOM sobrevive al cierre del editor).
        if (_paytefTerminal){
            try {
                this.data.paytefAutoprint.requestPrint(this, _paytefTerminal, _ticketReady, _commitReady);
            } catch(e){
                console.error("[TICKET] paytefAutoprint.requestPrint failed", e);
            }
        }

        if (this._print_requested){
            this.DoRefresh('TICKET', true);

            this._print_requested = false;

            const _receiptData = {
                isauto: this._onpay_requested,
                drawer: this._dopen_requested,
                // En datafono cuando el overlay paytef ya esta imprimiendo
                // el receipt en el terminal, no duplicar la copia por la
                // impresora BAR (solo abrir cajon).
                paytefAutoprint: !!_paytefTerminal
            };

            // El receipt de cobro va por el overlay root-level: el
            // ui-ticket-print del editor muere con el race del emit y
            // en BARRA no hay thumb en tab-tickets aun. El overlay
            // vive en app.component y sobrevive al cierre del editor.
            this.data.paytefAutoprint.requestBarReceipt(this, _receiptData, _ticketReady, _commitReady);
        }

        // Emite OnCommit ANTES de LogTicketChange para que el editor
        // cierre al instante. ui-change-print schedule su setTimeout(0)
        // sincronicamente y awaita commitReady dentro — si falla el
        // backend, la comanda no sale (sin papel fantasma).
        this._onCommit.next({
            commitReady: _commitReady,
            claim: _makePrintClaim()
        });

        // save the current list of products (to avoid refresh on update)
        this._lastproducts.clear();
        for (let _ticketproduct of this.products){
            this._lastproducts.set(_ticketproduct, _ticketproduct.hash);
        }

        // super.DoCommit necesita las filas del change completas y
        // objid/orderno resueltos con _toInsert=false. Awaitamos ambas
        // promesas — si CreateTicket o LogTicketChange fallan,
        // abortamos antes del super.DoCommit (evita ticket sin
        // trazabilidad fiscal + duplicacion por do_insert).
        let _ticketReadyOk = true;
        try {
            await _createPromise;
            await _ticketReady;
        } catch(e) {
            console.error("[TICKET] pre-commit ready failed — aborting commit", e);
            _ticketReadyOk = false;
        }

        let _result: boolean;

        if (!_ticketReadyOk){
            // Abort commit — no llamar super.DoCommit ni AddLastTicket.
            _result = false;
            this._lastCommitFailure = 'fiscal';
            console.error("[TICKET] cobro NO registrado (fallo fiscal antes de enviar): no se reintenta solo", { objid: this.objid, series: this._ticket.series, invceno: this._ticket.invceno, payment: this._ticket.payment });
            _commitReject(new Error("_ticketReady failed"));
        }
        else {
            // calculate the ticket taxes (just before commit) — usa SplitTaxes
            // que lee del change ya creado por LogTicketChange (de ahi que
            // tenga que ir DESPUES del await _ticketReady).
            let _taxes = 0;
            for (let _splittax of this.SplitTaxes){
                _taxes += _splittax['taxes'];
            }
            this.taxes = _taxes;

            // TEMP diag — factura sin lineas: solo si el ticket llega SIN lineas
            // justo antes de super.DoCommit (la anomalia). Loguearlo en todo
            // commit seria ruido en produccion. Quitar tras diagnosticar.
            try {
                // Misma condicion que FIX A (IsEmpty = sin lineas validas), no
                // products.length: si hubiera TicketProduct invalidos en el
                // array, products.length!=0 pero el ticket es "sin lineas".
                if (this.IsEmpty){
                    console.warn('[TICKET-COMMIT] IsEmpty antes de super.DoCommit', {
                        objid: this.objid,
                        uuid: (this as any)._uuid,
                        orderno: this._ticket.orderno,
                        series: this._ticket.series,
                        invceno: this._ticket.invceno,
                        status: this._ticket.status,
                        payment: this._ticket.payment,
                        price: this._ticket.price,
                        products: this.products.length,
                        changes: this.changes.length
                    });
                }
            } catch(_e){ /* diag best-effort */ }

            // FIX A — red de seguridad contra facturas numeradas SIN lineas: si
            // una carrera vacio this.products entre el snapshot (_lastproducts,
            // capturado antes de los awaits de arriba) y este punto, recuperar
            // las lineas del snapshot para que super.DoCommit las persista. No
            // pierde la venta ni deja una factura simplificada numerada con 0
            // lineas. Inofensivo si los productos siguen ahi (IsEmpty=false ->
            // no entra).
            if (this._ticket.series && this._ticket.invceno && this.IsEmpty
                    && this._ticket.status != 'CC'
                    && this._lastproducts && this._lastproducts.size > 0){
                let _recovered = 0;
                for (let _tp of Array.from(this._lastproducts.keys())){
                    if (_tp && _tp.IsValid){
                        this.AddProduct(_tp);
                        _recovered++;
                    }
                }
                if (_recovered > 0){
                    console.error('[TICKET] FIX-A recuperadas ' + _recovered + ' linea(s) perdida(s) antes del commit', {
                        objid: this.objid,
                        orderno: this._ticket.orderno,
                        series: this._ticket.series,
                        invceno: this._ticket.invceno
                    });
                    // recomputar taxes tras recuperar las lineas
                    let _rtaxes = 0;
                    for (let _splittax of this.SplitTaxes){
                        _rtaxes += _splittax['taxes'];
                    }
                    this.taxes = _rtaxes;
                }
            }

            // Invariante fiscal: un ticket numerado (serie+invceno) NO puede
            // committearse sin sus lineas. Un commit anterior pudo limpiar el
            // dirty-tracking de los hijos (base.ts _CommitMap limpia _toInsert/
            // _toUpdate al construir el batch, antes del ACK) sin que el server
            // llegara a confirmarlos (write perdido) -> _inCommit colgado y las
            // lineas no se reenvian jamas, quedando la cabecera fiscal sola.
            // Forzamos aqui el re-envio de todo hijo NO confirmado para que
            // persista atomico con la cabecera. Server deduplica por _uuid.
            // includeUnconfirmed=true: tambien los hijos con _inCommit ya a false pero
            // sin eco CONFIRMADO (_inCommitSince): una fila de lectura los pone a
            // _inCommit=false sin confirmar nada (Bugbot #1838). Reenviar de mas es
            // seguro: el servidor sella `updated` con la referencia nueva del envio y
            // check_replace admite >=, asi que el reenvio se aplica y recibe su eco.
            if (this._ticket.series && this._ticket.invceno && this._ticket.status != 'CC'){
                this.ForceRecommit(true);
            }

            // commit the ticket
            try {
                _result = await super.DoCommit(force);
            } catch(e) {
                _result = false;
                console.error("[TICKET] super.DoCommit threw", e);
            }

            this._lastCommitFailure = _result ? null : 'rejected';
            if (_result){
                this.place.AddLastTicket(this);
                console.info("[TICKET] Commited ticket [" + this.objid + "] with flags [" + this._ticket.flags + "] completed in " + (performance.now() - _ini).toFixed(2) + " ms");
                _commitResolve();   // prints proceden
            }
            else {
                _commitReject(new Error("super.DoCommit returned false"));   // prints skip
            }
        }

        this._dopen_requested = false;
        this._onpay_requested = false;

        // unlock other commits on this ticket (after this one is commited)
        if (this._ticketInCommit){
            let _lock = this._ticketInCommit;   // captura: si otro commit toma el lock
                                                // en estos 100ms, no lo pisamos ni petamos
            setTimeout(() => {
                _lock.next();                       // libera SIEMPRE a quien espera este lock
                if (this._ticketInCommit === _lock){
                    this._ticketInCommit = null;    // solo lo limpia si nadie tomó uno nuevo
                }
            }, 100)
        }

        return _result;
    }

    /****************************/
    /* TICKET PAID CONTROL      */
    /****************************/

    private _onPayment = new Subject<any>();
    public OnPayment = this._onPayment.asObservable();

    // Cobro en vuelo: el eco de nuestro write solo lo confirma si vuelve PAGADO. Si el
    // server ignoro el update (check_replace: fila mas nueva de otro dispositivo) el eco
    // trae la fila sin pagar: el guard de _patchData conserva el PD local y el rescate
    // (dataService) debe seguir viendo el cobro como no confirmado para reenviarlo.
    // Una fila de LECTURA (sin _actn) tambien confirma si viene pagada con NUESTRO
    // numero de factura Y nuestro mismo estado y forma de pago: el POST se aplico pero
    // su respuesta se perdio (timeout/504); sin esto el rescate reenviaria un cobro
    // que el server ya tiene. Exigir status+payment evita que una fila ANTIGUA (p.ej.
    // aun en efectivo) confirme un cambio pendiente de forma de pago rechazado. Un
    // falso negativo solo cuesta un reenvio, que el server aplica y echoa.
    // Excepcion: misma factura y estado pero OTRA forma de pago, cuando el SERVIDOR ha
    // cambiado la forma de pago desde la ultima fila que este ticket aplico (y la fila es
    // posterior a ella). Eso no es una lectura antigua: es un cambio de forma de pago
    // hecho en otro dispositivo sobre nuestro cobro (que por tanto si llego al servidor).
    // Sin confirmarlo, el snapshot de _PendingPaymentSnapshot reimponia nuestra forma de
    // pago y el rescate la reenviaba, pisando en el servidor el cambio del otro sin aviso.
    // NO basta con que la fila sea "mas nueva": un touch (do_touch, `updated = NOW()` sin
    // cambiar el contenido) o una edicion de otro campo traen la MISMA forma de pago que
    // ya habiamos aplicado, y confirmarla apagaria el rescate de nuestro cambio rechazado.
    // Y tiene que ser POSTERIOR a nuestro envio pendiente (_inCommitRef, referencia de
    // servidor del ultimo lote): una fila de otro dispositivo ANTERIOR a nuestro cambio
    // que llega con retraso (B pasa a efectivo, luego A cambia a tarjeta y su lote se
    // rechaza, y la fila de B llega despues) no puede confirmar el cambio de A: el de A
    // es el mas reciente y el rescate debe reenviarlo (el servidor haria lo mismo con
    // check_replace). Sin fila aplicada (tras recargar) o sin referencia del envio se
    // mantiene el criterio estricto.
    protected _EchoConfirmsCommit(info, isOwnEcho: boolean): boolean {
        if (!info){
            return false;
        }
        if (!this.IsPaid){
            return isOwnEcho;
        }
        // Mismo criterio que el guard de _patchData (_wasPaid/_nowPaid). 'RD' NO es
        // pagado: OnPrepare lo pone en tickets sin cobrar (listo para servir); un eco
        // retrasado de esa preparacion no debe dar por confirmado un cobro posterior.
        let _echoPaid = (['PD', 'PP'].includes(info['status']))
                     || !!info['payment']
                     || this._flagIsSet(info['flags'], this.TicketFlags.TicketPaid);
        if (isOwnEcho){
            return _echoPaid;
        }
        let _sameInvoice = _echoPaid && !!this._ticket.invceno
            && (info['invceno'] == this._ticket.invceno) && (info['series'] == this._ticket.series)
            && (info['status'] == this._ticket.status);
        if (!_sameInvoice){
            return false;
        }
        if ((info['payment'] || null) == (this._ticket.payment || null)){
            return true;
        }
        return this._ServerChangedPayment(info);
    }

    // ¿Trae la fila un cambio de forma de pago hecho en el SERVIDOR desde la ultima fila
    // que este ticket aplico? Exige las dos cosas: que sea posterior a ella y que su forma
    // de pago sea distinta de la de ella. Sin dato fiable (sin fila aplicada, sin
    // 'updated', fecha ilegible) -> false: se conserva el criterio estricto.
    private _ServerChangedPayment(info): boolean {
        let _lastUpdated = this._LastApplied('updated');
        if ((_lastUpdated === undefined) || !_lastUpdated || !info['updated']){
            return false;
        }
        let _rowTs  = Date.parse(this.mysqlToDateStr(info['updated']));
        let _lastTs = Date.parse(this.mysqlToDateStr(_lastUpdated));
        if (isNaN(_rowTs) || isNaN(_lastTs) || !(_rowTs > _lastTs)){
            return false;
        }
        // Posterior a NUESTRO envio pendiente (mismo reloj: referencia de servidor, en s).
        if ((this._inCommitRef === null) || (this._inCommitRef === undefined) || !(_rowTs > this._inCommitRef * 1000)){
            return false;
        }
        return (info['payment'] || null) != (this._LastApplied('payment') || null);
    }

    protected patchValue(dst, property, value){
        let _status = (property == 'status') ? dst[property] : null;
        let _change = super.patchValue(dst, property, value);

        if (_change && (property == 'status') && (_status != null) && (dst[property] == 'PD')){
            this._onPayment.next();     // notify ticket payment
        }

        return _change;
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    protected _UpdateFlags(){
        this.DoChange("set", null);     // trigger the ticket change

        let _flags = this._ticket.flags ? this.flags : [];

        // update the ticket product status
        for(let _ticketproduct of this.products){
            _ticketproduct.status = this.status;
        }

        // remove the ticket extras (for unwanted ones) - on ticket payment
        if (this.status == 'PD'){
            for(let _ticketextra of this.extras){
                if (_ticketextra.status == 'UN'){
                    _ticketextra.status = 'DE';
                }
            }
        }

        // remove the ticket extras (non-product dependant) - on ticket cancelation
        if (this.status == 'CC'){
            for(let _ticketextra of this.extras){
                if (_ticketextra.extra.type == 'TICKET'){
                    _ticketextra.status = this.IsPaid ? 'UN' : 'DE';
                }
            }
        }

        // remove the ticket extras (non-product dependant) - on ticket deletion
        if (this.status == 'DE'){
            for(let _ticketextra of this.extras){
                if (_ticketextra.extra.type == 'TICKET'){
                    _ticketextra.status = 'DE';
                }
            }
        }

        // ready: all products are ready
        _flags[this.TicketFlags.TicketReady] = this.products.every(
        _ticketproduct => {
            return (_ticketproduct.IsValid == false) || _ticketproduct.HasEvent('RD');
        });

        // TicketToPay: server lo gestiona durante un Stripe en vuelo
        // (checkout.php lo setea, on-failure / update_ticket_status('PD')
        // lo limpia). Por eso NO se recomputa aqui en general — un
        // recompute mientras status='AC' o 'PP' pisaria el "1" del
        // server. Excepcion: cuando el ticket llega a un estado terminal
        // (PD/CC/DE) la fase de Stripe ya termino, asi que limpiamos el
        // flag — sin esto el pago a cuenta (que pasa por status='PD' sin
        // tocar update_ticket_status server-side) deja el flag a 1 y
        // IsToPay sigue diciendo "pendiente".
        if (['PD', 'CC', 'DE'].includes(this.status)){
            _flags[this.TicketFlags.TicketToPay] = false;
        }

        // paid: hay AL MENOS un producto valido y TODOS los validos estan
        // pagados (evento PD). El some(IsValid) es imprescindible: sin el,
        // un ticket VACIO (recien creado) o con todos los productos cancelados
        // daba TicketPaid=true por la verdad vacua de [].every() -> IsPaid=true
        // en tickets NO pagados (un PR nuevo entraba al short-circuit de pagado
        // y mentia como "pagado" hasta anadir el primer producto).
        _flags[this.TicketFlags.TicketPaid] = this.products.some(
            _ticketproduct => _ticketproduct.IsValid
        ) && this.products.every(
        _ticketproduct => {
            return (_ticketproduct.IsValid == false) || _ticketproduct.HasEvent('PD');
        });

        this.flags = _flags;
    }

    protected _RemoveRequests(){
        if (this.qrcode){
            // remove all pending waiter requests
            if (this.request && ([ 'PAYCASH', 'PAYCARD', 'PAYMIXED', 'REFUND' ].includes(this.request.reason))){
                this.request.status = 'DE';     // remove any pending payment requests
            }
            
            this.qrcode.DoRefresh('TICKET');    
        }
    }

    get status(): string {
        return super.status;
    }

    set status(value: string){
        // add the ready status for direct tickets
        if (value == 'PD' && this.IsDirect){
            this.status = 'RD';
        }

        if (value != this._ticket.status){
            let _reopen = (['PR', 'AC'].includes(value) && (this.status != 'PR'));

            // remove any pending waiter requests (before changing status)
            if (['AC', 'PD', 'DE', 'CC'].includes(value)){
                this._RemoveRequests();
            }

            super.status = value;

            // update the status for the ticket products and flags
            this._UpdateFlags();

            // paid timestamp considerations
            if (value == 'PD'){
                this.paidon = new Date();
                this.paidin = this.data.session;
            }

            if (_reopen){
                this.payment = null;
                this.given = null;
                this.paidby = null;
                this.paidon = null;
                this.paidin = null;
            }

            // payment considerations
            if (value == 'PP'){
                this.paidby = this.data.session;
            }
            
            this.DoRefresh('TICKET', true);
            if (this.place){     // force place refresh on status change
                this.place.DoRefresh('TICKET', true); 
            }
        }
    }

    get payment(): string {
        let _payment = super.payment;
        
        switch(_payment){   // backwards compatibility
            case 'FORCEPAY':
            case 'CASHPAY':
                return 'PAYCASH';
            case 'CARDPAY':
                return 'PAYCARD';
            case 'PAYPAL':
                return 'PAYLINE';
        }

        if (_payment == null){
            return (this.IsPaid) ? 'UNKNOWN' : null;
        }

        return _payment; 
    }

    set payment(value: string){
        if (value && (!this.IsPaid || this.IsToPay) && (this.invceac == null)){
            // In datáfono mode the app runs on the payment terminal itself —
            // there's no cash drawer wired to this device. The OpenOnCash /
            // OpenOnCard flags belong to the main POS at the bar and must
            // NOT fire a remote drawer-open on every charge done from the
            // terminal, or the bar drawer would pop every time.
            // PAYMIXED honours both OpenOnCash and OpenOnCard because
            // mixed splits the total between cash and card — treating it
            // as neither used to skip the drawer even when cash was given.
            let _isDatafono = !!(this.data && this.data.platform && this.data.platform._isDatafono);
            let _iscash = (value == 'PAYCASH') || (value == 'PAYMIXED');
            let _iscard = (value == 'PAYCARD') || (value == 'PAYMIXED');
            let _oncash = !_isDatafono && _iscash && this.place.OpenOnCash;
            let _oncard = !_isDatafono && _iscard && this.place.OpenOnCard;

            this.DoPrint(_oncash || _oncard, true, true);

            // Paytef terminal auto-print on PAYCASH — ONLY when the app
            // itself is running on the terminal (datáfono mode). A TPV
            // configured with a paytef terminal for cobros must NOT
            // print on the terminal when the cashier hits "efectivo" —
            // that goes to the bar printer as usual. The terminal's
            // built-in printer is meant for receipts of operations
            // done physically on the terminal, not routed from a TPV.
            //
            // Stash the terminal via RequestPaytefAutoPrint instead of
            // firing ToPaytefPrinter here directly: the setter runs
            // BEFORE DoCommit → LogTicketChange, so printing now would
            // render the ticket in its draft state (no series / invceno
            // / 'PD' status / TicketBAI QR). It would also double-print,
            // because DoPrint(oncommit=true) above still schedules the
            // regular post-commit print path. DoCommit consumes the
            // stashed terminal after LogTicketChange and merges it into
            // the single _onPrint emit — one receipt, correctly paid.
            if (value == 'PAYCASH' && this.place && this.data && this.data.platform && this.data.platform._isDatafono){
                for (let _service of this.place.services){
                    if (!_service.payment_terminals) continue;
                    for (let _terminal of _service.payment_terminals){
                        // Solo PAYTEF imprime recibos: un PROSEGUR/CASHLOGY con
                        // onpaycash mandaria el PRINT a un alias del cajon (no IP
                        // Paytef) y fallaria el auto-print.
                        if (_terminal.status == 'AC' && _terminal.onpaycash && _terminal.IsPaytef){
                            this.RequestPaytefAutoPrint(_terminal);
                            break;  // one terminal per PAYCASH — first match wins
                        }
                    }
                    if (this._pendingPaytefTerminal) break;
                }
            }
        }

        if (value != this._ticket.payment){
            super.payment = value;
        }

        if (value != null){
            this.status = (value == 'PAYACCOUNT') ? 'PP': 'PD';
        }
        else {  // payment has been removed
            this.paidby = null;
            this.given = null;
            this.paidon = null;
            this.paidin = null;
        }
    }

    get account() : PayAccount {
        return super.account;
    }

    set account(value: PayAccount) {
        super.account = value;
        if (value){
            super.payment = 'PAYACCOUNT';
        }
    }

    get comments(): string{
        return super.comments;
    }

    set comments(value: string){
        if (this.comments != value){
            this._StampWaiter();              // "último que edita" (comentario del ticket)
            super.comments = value;
            this.DoChange("cmt", null);       // trigger the comment change (ticket-change)
        }
    }

    get source(): string {  // 'BR': BAR, 'SF': STAFF, 'QR': QRCODE (CLIENT), 'HM': HOME (WEB)
        let _source = 'BR';
        if (this.table && this.session){
            _source = (this.session.user) ? 'SF' : 'QR';
        }
        return _source;
    }

    get products() : Array <TicketProduct> {
        let _ticketproducts = [];

        // do not return other ticket products (divided order)
        for(let _product of super.products){
            if (_product.ticket == this){
                _ticketproducts.push(_product)
            }
        }

        return _ticketproducts;
    }

    /**
     * Lineas reales del ticket, sin entradas repetidas.
     *
     * Mientras se reconcilia con el server el ticket puede tener
     * transitoriamente DOS entradas de la misma linea: la copia optimista y la
     * fila persistida que vuelve por sync. La lista impresa ya deduplicaba
     * (ui-print._GetTicketItems), pero los sumatorios de dinero no, asi que el
     * total contaba la linea dos veces —una con el importe de antes de
     * modificarla— mientras solo se imprimia una. Eso inflaba tambien la base
     * imponible, porque SplitTaxes alimenta el desglose de la factura.
     *
     * Mismo criterio que la impresion: se descarta si repite CUALQUIERA de los
     * dos identificadores. `refid` (_uuid) caza la pareja optimista+persistida
     * (mismo _uuid, distinto objid) y `objid` (la PK) caza dos instancias de la
     * misma fila persistida que acabaron con _uuid distintos. No hay falso
     * merge: dos filas distintas nunca comparten objid ni _uuid, y `refid`
     * siempre tiene valor (el getter lo genera si falta).
     */
    get UniqueProducts() : Array <TicketProduct> {
        const _unique: Array <TicketProduct> = [];

        const _seenrefid = new Set<string>();
        const _seenobjid = new Set<string>();

        for(let _product of this.products){
            let _refid = _product.refid;
            let _objid = _product.objid;

            let _dup = _seenrefid.has(_refid) || (_objid && _seenobjid.has(_objid));

            // registrar SIEMPRE ambos identificadores, aunque se salte la fila
            _seenrefid.add(_refid);
            if (_objid){
                _seenobjid.add(_objid);
            }

            if (_dup){
                continue;   // ya contada (por refid o por objid)
            }

            _unique.push(_product);
        }

        return _unique;
    }

    get change(): TicketChange {
        if (this.changes.length > 0){
            let _changes = this.changes.sort((a, b) => {
                return b.created.getTime() - a.created.getTime();
            });

            return _changes[0];     // this is the last change
        }

        return null;
    }

    get ticketbai(): TicketBAI {
        // ticketbai entry for the last change
        let _change = this.change;
        if (_change){
            return _change.ticketbai;   
        }

        return null;    // no changes
    }

    get verifactu(): VeriFactu {
        // ticketbai entry for the last change
        let _change = this.change;
        if (_change){
            return _change.verifactu;   
        }

        return null;    // no changes
    }

    get ticketsii(): TicketSII {
        // ticketsii entry for the last autit
        let _changes = this.changes.sort((a, b) => {
            return b.created.getTime() - a.created.getTime();
        });

        for(let _change of _changes){
            if (_change.reason == 'T'){
                return _change.ticketsii;
            }
        }

        return null;    // no till changes
    }

    get client(): number {
        if (super.client){
            return super.client;
        }

        if (this.Client){
            return Number(this.Client.objid);
        }

        return null;
    }

    // Delegar al setter base: los loaders de informe aplican aqui el 'client'
    // computado por ticketlst.php (via ParseTicket), tambien a tickets ya
    // cargados. Sin este setter la propiedad seria de solo lectura.
    set client(value: number){
        super.client = value;
    }

    /****************************/
    /* CUSTOM MEMBERS           */
    /****************************/

    private _isopen: boolean = false;
    get isopen(){
        return this._isopen;
    }

    set isopen(value: boolean){
        this._isopen = value;
    }

    get request(): AskWaiter {
        if (this.qrcode){
            for(let _askwaiter of this.qrcode.requests){
                if ((_askwaiter.ticket == this) && (_askwaiter.IsValid)) {
                    return _askwaiter;
                }
            }
        }
        return null;
    }

    get requests(): Array<AskWaiter> {
        let _requests = [];
        
        if (this.qrcode){
            for(let _askwaiter of this.qrcode.requests){
                if (_askwaiter.ticket == this) {
                    _requests.push(_askwaiter);
                }
            }
        }
        return _requests;
    }

    set request(value: AskWaiter){
        if (this.qrcode){
            let _askwaiter = this.request;
            if (_askwaiter){
                if (!value){    // remove the existing request
                    _askwaiter.status = 'DE';
                }
            }
    
            if (value){
                this.qrcode.AddRequest(value);
            }
        }
    }

    get invoice() : TicketInvoice {
        for(let _invoice of this.invoices){
            if (_invoice.IsValid){
                return _invoice;
            }
        }

        return null;
    }

    set invoice(value: TicketInvoice){
        for(let _invoice of this.invoices){
            _invoice.status = 'DE';
        }

        if (value != null){
            this.AddInvoice(value);
        }

        this.DoRefresh('TICKET', true);
    }

    private _pending = null;
    get pendpay(){
        return this._pending;
    }

    set pendpay(value){
        this._pending = value;
    }

    get pendrf(){   // this is the ticket refund without discounts
        return this.TicketControl.refund;
    }

    private _tocharge: number = null;
    get _charge(){
        return this._tocharge || 0;
    }

    set _charge(value){
        this._tocharge = value;
    }

    private _torefund: number = null;
    get _refund(){
        return this._torefund || 0;
    }

    set _refund(value){
        this._torefund = value;
    }
        
    get audit(): Audit {
        // for old tickets
        if (this.intill){
            return this.intill;
        }

        // for new tickets
        let _audit = this.audits;
        if (_audit.length > 0){
            return _audit[0].audit;
        }
        return null;
    }

    set audit(value: Audit){
        let _audit = this.audit;
        if (_audit){
            console.warn("[TICKET] Cannot change this ticket's audit! Operation ignored");
        }
        else {
            let _ticketaudit = new TicketAudit(null, this.data);
            if (_ticketaudit){
                _ticketaudit.ticket = this;
                _ticketaudit.audit = value;
                _ticketaudit.status = 'AC';
            }

            this.AddChild('audit', _ticketaudit, 'ticket');
        }
    }

    get duedate(): Date {
        if (this.IsCancelled || (this.IsPaid && (this.payment != 'PAYACCOUNT'))){
            return null;
        }

        let _date = null;
        if (this.payment == 'PAYACCOUNT'){
            _date = new Date(this._ticket.created);
            _date.setDate(1);
            _date.setMonth(_date.getMonth() + 1);

            return _date;
        }
        else {
            if (this.invoice){
                _date = new Date(this.invoice.created);
            }
            else {
                _date = new Date(this._ticket.created);
            }
            
            _date.setDate(_date.getDate() + this.duedays);
            return _date;    
        }
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get BarTicket(){
        return (this.qrcode == null);
    }

    get IsRecent(){
        let _recent = false;

        if (!_recent){                  // check by creation time
            _recent = (this.created.getTime() > ((new Date()).getTime() - AppConstants.recentMs));
        }

        if (!_recent && this.paidon){   // check by paidon time
            _recent = (this.paidon.getTime() > ((new Date()).getTime() - AppConstants.recentMs));
        }

        return _recent
    }

    get IsDirect(){
        if (!this.place.IsDirect){
            return false;   // the place has track ticket enabled (ticket is not direct)
        }

        if (this.source == 'QR'){
            return !this.place.GetConfigOption('optionQrTrackTicket');
        }

        return true;
    }

    get IsPrepay(){
        if (this.place.IsPrepay){
            return true;
        }

        if (this.source == 'QR'){
            return this.place.GetConfigOption('optionQrPrePayment');
        }

        return false;
    }

    get IsEmpty(){  // same as (Length == 0)
        return !this.products.some(
        (_ticketproduct) => {
            return _ticketproduct.IsValid;
        });
    }

    get Length(){
        let _length: number = 0;

        for(let _ticketproduct of this.products){
            if (_ticketproduct.IsValid && !_ticketproduct.Separator){
                _length++;
            }
        }

        return _length;
    }

    get IsActive(){
        switch(this._ticket.status){
            case 'PR':
                return false;

            case 'CC':
                return !!this.ToRefund;

            case 'DE':
                return false;
        }

        return true;    
    }

    get IsValid(){
        let _valid = this.IsLoaded;

        if (!_valid){    // check if insert is pending
            _valid = !this._isCatalog && (this._toInsert || this._toCommit || this._inCommit);
        }

        if (_valid) {
            return this.IsActive;
        }

        return false;
    }

    get IsReady(){
        return (this.IsDirect) || (this.status == 'RD') || this._GetFlagValue(this.TicketFlags.TicketReady);
    }

    get IsPaid(){
        return (this.status == 'PD') || (this.account != null) || this._GetFlagValue(this.TicketFlags.TicketPaid);
    }

    get IsToPay(){
        return (this.status == 'PP') || this._GetFlagValue(this.TicketFlags.TicketToPay);
    }

    /**
     * true si el ticket esta pagado y el pago lo hizo OTRA sesion (QR prepago
     * via Stripe, u otro TPV). paidin/paidby los estampa la sesion que cobra
     * (setter de payment/status) o el server (Stripe) y viajan en la MISMA
     * fila de sync que el status PD. Compara por objid (children o raw id en
     * _ticket — DoPatchValues puede disparar OnPayment antes de SetChild).
     */
    get PaidByAnotherSession(): boolean {
        if (!this.IsPaid){
            return false;
        }

        let _paidId = (this.paidin && this.paidin.objid)
            || (this.paidby && this.paidby.objid)
            || this._ticket.paidin
            || this._ticket.paidby;
        let _ownId = this.data.session && this.data.session.objid;

        return !!(_paidId && _ownId && (_paidId != _ownId));
    }

    /**
     * Dueño del PROCESADO de un ticket pagado por otra sesion (QR prepago):
     * el cliente QR vuelve de Stripe a on-payment/success y el carrito ya no
     * monta ui-change-print para tickets PD (pasan a recibo) — alguien del
     * local debe imprimir la comanda y emitir el 'F'. Un solo POS:
     *  - si la mesa tiene till: el POS cuyo place.till coincide (por objid)
     *  - si no: el POS con till de menor objid (eleccion determinista; el
     *    fallback anterior `IsPOSDevice` hacia que TODOS los POS procesaran)
     */
    get IsPaidProcessOwner(): boolean {
        if (!this.place || !this.place.IsPOSDevice){
            return false;
        }

        let _owntill = this.place.till;
        if (!_owntill || !_owntill.objid){
            return false;
        }

        let _tabletill = this.qrcode ? this.qrcode.till : null;
        if (_tabletill && _tabletill.objid){
            return (_owntill.objid == _tabletill.objid);
        }

        // Sin till de mesa: un unico ganador entre tills que tienen al
        // menos un connect configurado como POS (optionPosTerminal).
        // possvc incluye cualquier servicio AC (p.ej. comandero); sin este
        // filtro un comandero con menor objid podia "ganar" y ningun POS
        // seria dueño → todos a gracia y carreras multi-dispositivo.
        let _winnerId = null;
        for (let _svc of this.place.possvc){
            if (!_svc || !_svc.IsValid || _svc.objid == null){
                continue;
            }
            let _isPosTill = (_svc.connects || []).some(
                (_c) => _c && _c.IsValid && _c.IsPosDevice
            );
            if (!_isPosTill){
                continue;
            }
            if (_winnerId == null || Number(_svc.objid) < Number(_winnerId)){
                _winnerId = _svc.objid;
            }
        }

        return (_winnerId != null) && (_owntill.objid == _winnerId);
    }

    /**
     * Ventana de gracia del dueño: margen para que el TPV dueño procese el
     * pago remoto y su TICKETCHANGE + flag printed sincronicen al resto
     * (peor caso del long-poll ~20s + margen). Pasada la ventana sin que
     * nadie lo haya procesado (dueño offline), cualquier dispositivo puede
     * actuar de respaldo — el primero gana.
     */
    get RemotePaidGraceElapsed(): boolean {
        let _paidon = this.paidon;
        if (!_paidon){
            return true;    // sin timestamp: no bloquear indefinidamente
        }

        return ((new Date()).getTime() - _paidon.getTime()) > 30000;
    }

    get IsCancelled(){
        if ((this.status != 'PD') && (this.products.length == 0) && !this.IsVolatile){
            return this.IsLoaded || (this._toInsert || this._toCommit || this._inCommit);
        }

        switch(this.status){
            case 'DE':
            case 'CC':
                return !(this.ToRefund) && (this.ProductsCancelled.length > 0);
        }

        return false;
    }

    get IsWebPay(){
        return (this.IsToPay) && (this.payment == 'PAYLINE'); 
    }

    get IsInvoice(){
        return (this.invoice != null);
    }
    
    get ToInvoice(){
        return !this.IsCancelled && (this.price >= AppConstants.InvoiceMaxPrice);
    }

    InSession(session){
        return (this.session == session) && (this.IsValid) && (this.IsRecent)
    }

    InAudit(ini, end, audit){
        // A ticket already assigned to another audit never belongs here.
        if (this.audit && (this.audit != audit)){
            return false;
        }

        // Mirror the authoritative server-side filter (auditinfo.php): a
        // not-cancelled ticket counts only if it was PAID within the period
        // (paidon in (ini, end]); a cancelled ticket counts by its
        // cancellation time (updated in (ini, end]). An open (uncharged)
        // ticket — paidon == null — must NOT fall into every audit: doing so
        // inflated the cierre report (completados/productos/numeracion) with
        // tickets still open on the tables, while "TOTAL VENTAS" stayed
        // correct because an open ticket has payment == null. The lower bound
        // is exclusive (> ini) to match the server's `> prev`, so a ticket on
        // the exact boundary of two consecutive closes lands in one only.
        let _inaudit;
        if (['CC', 'DE'].includes(this.status)){
            let _updated = this.updated;
            _inaudit = (_updated != null) && (_updated > ini) && (_updated <= end);
        }
        else {
            let _paidon = this.paidon;
            _inaudit = (_paidon != null) && (_paidon > ini) && (_paidon <= end);
        }

        if (_inaudit && this.place.MultiplePOS){
            let _included = false;

            // check if it has been paid on a till (include if paid on this one)
            if (!_included && this.paidin) {
                let _tillpaid = this.place.services.some(_till => {
                    return _till.PaidIn(this);
                });

                if (_tillpaid){     // only include if it has been paid on this till
                    return (this.place.till.PaidIn(this));
                }
            }

            // check if the ticket belongs to this till tables
            if (!_included){
                if (this.qrcode != null){
                    _included = (this.place.till.tables.includes(this.qrcode))
                }
            }

            // check if its a bar ticket created on this till
            if (!_included){
                if (this.qrcode == null){
                    _included = this.place.till.connects.some(_connect => {
                        return _connect.IsValid && (_connect.device == this.device);
                    });
                }
            }

            _inaudit = _included;
        }

        return _inaudit;
    }

    get OrderNo(){
        if (this._ticket.orderno){
            let _number_pos = this._ticket.orderno.indexOf('-');
            if (_number_pos != -1){
                return this._ticket.orderno.substring(_number_pos + 1); 
            }
        }
        return this._ticket.orderno;               
    }

    get TableNo(){
        if (!this.qrcode){
            return null;    // bar ticket
        }
        // Mostrar el ALIAS de la mesa si lo tiene, si no el número. Preferimos la
        // mesa desde place.tables (lleva el alias aunque el qrcode del ticket venga
        // "fino", p.ej. en comandas / listados); si no, el propio qrcode del ticket.
        let _resolve = (t: QrCode) => (t && t.alias && t.alias.trim())
            ? t.alias
            : (t && (t.number != null) ? String(t.number) : null);
        if (this.place && this.place.tables && this.place.tables.length){
            let _table = this.place.tables.find(t => t.objid == this.qrcode.objid);
            if (_table){
                return _resolve(_table);
            }
        }
        return _resolve(this.qrcode);
    }

    private _strPrice(price){
        let _price = {
            Str: "0,00",
            Int: "0",
            Dec: "00",
            Val: 0.0,
            Neg: false
        };

        if (price !== null){
            let _parts = Math.abs(price).toString().split('.');

            let _int = _parts[0];
            let _dec = (_parts.length > 1) ? (_parts[1] + '0').slice(0, 2) : "00";
            let _str = _int + ',' + _dec;
            let _val = price;
            let _neg = (price < 0); 

            _price = {
                Int: _int,
                Dec: _dec,
                Str: _str,
                Val: _val,
                Neg: _neg
            }    
        }
        return _price;
    }

    get Price(){
        return this._strPrice(this._ticket.price);
    }

    private _taxesfor(target){
        let _taxrates = {};

        switch(target){
            // split the total for products (not in offer)            
            case 'TP':    
                for (let _ticketproduct of this.UniqueProducts){
                    if (!_ticketproduct.IsValid || (_ticketproduct.status == 'DE') || _ticketproduct.offer){
                        continue;
                    }

                    let _taxrate = _ticketproduct.taxrate;
                    if (!(_taxrate in _taxrates)){
                        _taxrates[_taxrate] = 0;
                    }

                    _taxrates[_taxrate] += _ticketproduct.charge;
                }
                break;

            // split the total for offers (aplying place tax rate)
            case 'TO':
                for (let _ticketoffer of this.offers){
                    if (!_ticketoffer.IsValid){
                        continue;
                    }

                    let _taxrate = _ticketoffer.taxrate;
                    if (!(_taxrate in _taxrates)){
                        _taxrates[_taxrate] = 0;
                    }
        
                    _taxrates[_taxrate] += _ticketoffer.charge;
                }
                break;

            // split the total for extras (aplying place tax rate)
            case 'TE':
                for (let _ticketextra of this.extras){
                    if (!_ticketextra.IsValid){
                        continue;
                    }

                    let _taxrate = _ticketextra.taxrate;
                    if (!(_taxrate in _taxrates)){
                        _taxrates[_taxrate] = 0;
                    }
        
                    _taxrates[_taxrate] += _ticketextra.charge;
                }
                break;

            // split each discount by the aplying tax rates
            case 'TD':
                for (let _ticketdiscount of this.discounts){
                    if (!_ticketdiscount.IsValid){
                        continue;
                    }

                    let _dsctaxes = _ticketdiscount.SplitTaxes;
                    for (let _dsctax of _dsctaxes){
                        let _taxrate = _dsctax.rate;
                        if (!(_taxrate in _taxrates)){
                            _taxrates[_taxrate] = 0;
                        }

                        _taxrates[_taxrate] -= _dsctax.total;
                    }
                }
                break;
        }

        return _taxrates;
    }

    _combinedtaxes(...targets: ('TP' | 'TO' | 'TE' | 'TD')[]){
        let taxrates = [];
        for(let _target of targets){
            taxrates.push(this._taxesfor(_target));
        }

        const _combined = {};
        for (const obj of taxrates) {
            for (const [key, value] of Object.entries(obj)) {
                if (key in _combined) {
                    _combined[key] += value;
                } 
                else {
                    _combined[key] = value;
                }
            }
        }
        return _combined;
    }

    get SplitTaxes(){
        let _taxrates = this._combinedtaxes('TP', 'TO', 'TE', 'TD');

        let _totaxes = [];
        
        for (let _taxrate in _taxrates){
            let _total = _taxrates[_taxrate];
            let _base = _total / ((100 + Number(_taxrate))/100);
            let _taxes = _total- _base;
    
            _totaxes.push({
                rate: Number(_taxrate),
                total: Math.round(_total * 100)/100,
                base: Math.round(_base * 100)/100,
                taxes: Math.round(_taxes * 100)/100
            })
        }

        return _totaxes;
    }

    get TotalTaxes(){
        let _taxes = {
            total: 0,
            base: 0,
            taxes: 0
        };

        let _split = this.SplitTaxes;
        for (let _tax of _split){
            _taxes.total += _tax.total;
            _taxes.base += _tax.base;
            _taxes.taxes += _tax.taxes;
        }

        return _taxes;
    }

    get Client(){
        let _client = this._ticket.client;
        if (_client){
            for(let _business of this.place.clients){
                if (parseInt(_business.objid) == _client){
                    return _business;
                }
            }
        }
        else {
            if (this.invoice){
                return this.invoice.client;
            }

            if (this.invceac){
                return this.invceac.account.client;
            }

            if (this.account){
                return this.account.client;
            }
        }

        return null;
    }

    payAmount(payment){
        if (this.payment == payment){
            return this.price;
        }

        if (this.payment == 'PAYMIXED'){
            if (this._ticket.pmixed){
                let _mixed = [];

                let _payments = this._ticket.pmixed.split('|');
                for(let _payment of _payments){
                    let _parts = _payment.split(':');
                    _mixed[_parts[0]] = parseFloat(_parts[1]);
                }

                if ((payment in _mixed) && !isNaN(_mixed[payment])) {
                    return _mixed[payment];
                }

                return 0;
            }

            for(let _mixed of this.mixed){
                if (_mixed.payment == payment){
                    return _mixed.amount;
                }
            }
        }

        return 0;
    }

    /******************************/
    /* ORGANIZED TICKET ITEMS     */
    /******************************/

    get ProductsToSeparate(){
        return this.TicketControl.ProductsToSeparate;
    }

    get ProductsToPrepare(){
        return this.TicketControl.ProductsToPrepare;
    }

    get ProductsDelivered(){
        return this.TicketControl.ProductsDelivered;
    }

    get ProductsToRefund(){
        return this.TicketControl.ProductsToRefund;
    }

    get ProductsCancelled(){
        return this.TicketControl.ProductsCancelled;
    }

    get OffersToRefund(){
        return this.TicketControl.OffersToRefund;
    }

    get OffersToDeliver(){
        return this.TicketControl.OffersToDeliver;
    }

    get ExtrasToPrepare(){
        return this.TicketControl.ExtrasToPrepare;
    }

    get ExtrasToRefund(){
        return this.TicketControl.ExtrasToRefund;
    }

    get ExtrasToDeliver(){
        return this.TicketControl.ExtrasToDeliver;
    }

    get ToPrepare(){
        return this.TicketControl.ToPrepare;
    }

    get ToPayment(){
        return this.TicketControl.ToPayment;
    }

    get ToRefund(){
        return this.TicketControl.ToRefund;
    }

    get DiscountsToApply(){
        return this.TicketControl.DiscountsToApply;
    }

    get DiscountsToRefund(){
        return this.TicketControl.DiscountsToRefund;
    }

    /******************************/
    /* TICKET ACTIONS             */
    /******************************/

    private DoChange(operation, ticketproduct){
        this._onModify.next({
            operation: operation,
            product: ticketproduct
        })
    }

    get ToCancel(){
        return ['CC', 'DE'].includes(this.status);
    }

    DoCancel(silent: boolean = false){
        // Cancelación = acción humana (editor/vista/merge): atribuirla al camarero
        // seleccionado en el informe de actividad, no al último que editó el carrito.
        this._StampWaiter();

        // send notification before cancelling
        if (!silent){
            this._onCancel.next();
        }            

        if (this.IsEmpty){
            this.status = 'DE';
        }
        else {
            this.status = 'CC';
        }

        this.account = null;
    }

    DoUpdate(force = false){
        this._UpdateFlags();
        this.TicketControl.DoUpdate(force);

        this.DoRefresh('TICKET', true);     
    }

    ToCart(product: Product, options: Array <ProductOption>, info = null,  comments: string = null){
        let _pritem = null;
        let _fixedp = null;
        let _market = null;
        let _weight = null;  

        if (info != null){
            _pritem = 'pritem' in info ? info['pritem'] : null;
            _fixedp = 'fixedp' in info ? info['fixedp'] : null;
            _market = 'market' in info ? info['market'] : null;
            _weight = 'weight' in info ? info['weight'] : null;
        }

        let _ticketproduct: TicketProduct = new TicketProduct(null, this.data);
        if (_ticketproduct){
            _ticketproduct.ticket = this;
            _ticketproduct.sort = (_pritem) ? _pritem.sort : null;
            _ticketproduct.product = product;
            _ticketproduct.comments = comments;
            _ticketproduct.fixed = (_fixedp != null);
            _ticketproduct.charge = _fixedp;
            _ticketproduct.market = _market;
            _ticketproduct.weight = _weight;
            _ticketproduct.taxrate = product.taxrate;
            _ticketproduct.status = 'AC';
            _ticketproduct.waiter = this.data.CurrentWaiter;    // quién añadió esta línea

            for(let option of options){
                _ticketproduct.CartAdd(option);
            }

            _ticketproduct.price = _ticketproduct.TotalPrice;
            _ticketproduct.charge = _ticketproduct.ChargePrice
        }

        return _ticketproduct;
    }

    // Sellado público para las acciones HUMANAS que no pasan por Cart*/comentario:
    // cobrar, reabrir, cancelar. Sin esto la fila e0 del informe de actividad (cobro,
    // cancelación…) heredaba al ÚLTIMO que tocó el carrito —o al owner si la comanda
    // se creó sin camarero seleccionado— en vez de al camarero que HACE la acción
    // (CSV real 2026-10-06: OCT06-13 creada con waiter=140 (owner), cobrada por
    // Steven y la fila de cobro salía "amaru"). Misma regla que _StampWaiter: con PIN
    // el camarero seleccionado; sin lock, la sesión (como siempre); null no reasigna.
    StampWaiter(){
        this._StampWaiter();
    }

    // Sella el ticket con el camarero actual ("último que edita"). Se llama SOLO
    // desde mutaciones HUMANAS (Cart*/comentario), NUNCA desde sync/recálculo
    // (_OnUpdate/_UpdateFlags disparan DoChange, que no sella). Null en QR/guest
    // -> no reasigna. Idempotente: llamarlo varias veces en una op es inocuo.
    private _StampWaiter(ticketproduct: TicketProduct = null){
        let _w = this.data.CurrentWaiter;
        if (_w){
            this.waiter = _w;                   // "último que edita" el TICKET
            if (ticketproduct){
                ticketproduct.waiter = _w;      // y la LÍNEA modificada (no solo al
                                                // crearla en ToCart): si B edita una
                                                // línea de A, la línea pasa a B.
            }
        }
    }

    CartAdd(ticketproduct: TicketProduct){
        if (ticketproduct){
            this._StampWaiter();    // "último que edita" (añadir producto)
            this.AddProduct(ticketproduct);

            // notify the ticket change
            this.DoChange("add", ticketproduct);
        }

        // Force: rapid consecutive taps on products queue click events
        // back-to-back. The TicketUpdate reentrancy guard uses a
        // setTimeout(0) release, which runs AFTER the next queued click.
        // Without force, the second tap's DoUpdate no-ops and ticket.price
        // stays at only the first product — so a later "tarjeta" charges
        // a stale total. Cart ops are user-initiated and cannot be
        // debounced on the model side.
        this.DoUpdate(true);
    }

    CartDel(ticketproduct: TicketProduct, todelete: boolean = true){
        if (ticketproduct){
            this._StampWaiter(ticketproduct);    // "último que edita" (borrar producto)
            if (todelete){
                ticketproduct.status = 'DE';
            }

            // notify the ticket change
            this.DoChange("del", ticketproduct);
        }

        this.DoUpdate(true);
    }

    CartUdt(ticketproduct: TicketProduct){
        this._StampWaiter(ticketproduct);       // "último que edita" (update de producto)
        this.DoChange("set", ticketproduct);
    }

    /** Como CartUdt (update de un producto ya comandado) pero emite 'mod' en
     *  vez de 'set': el tracker de impresion lo saca bajo "MODIFICADOS" y los
     *  editores sobre copia (ui-edit) lo reproducen al ticket original como
     *  modificacion — sin par DE+AC. Lo usa CartModify (MODIFICAR-N) y el
     *  replay de ui-edit._ApplyChanges. */
    CartMod(ticketproduct: TicketProduct){
        this._StampWaiter(ticketproduct);       // "último que edita" (modificación de producto)
        this.DoChange("mod", ticketproduct);
    }

    CartSet(oldticketproduct: TicketProduct, newticketproduct: TicketProduct){
        // Sellar ambas líneas: el superviviente puede ser la nueva (camino DE+AC) o
        // la vieja (fast path in-place, mutada abajo).
        this._StampWaiter(newticketproduct);        // "último que edita" (sustituir producto)
        if (oldticketproduct && this.data.CurrentWaiter){
            oldticketproduct.waiter = this.data.CurrentWaiter;
        }
        // Fast path: viejo no persistido (ToInsert=true) + mismo Product
        // base → mutamos in-place. Evita fila DE fantasma en
        // TICKETPRODUCT, contador items_cancelled_amount inflado y
        // print cliente con "producto eliminado + nuevo producto".
        // Fallback al camino DE+AC cuando el viejo ya esta persistido
        // o la comanda ya salio (trazabilidad manda).
        let _canMutate = oldticketproduct
                      && newticketproduct
                      && oldticketproduct.ToInsert
                      && oldticketproduct.product === newticketproduct.product;

        if (_canMutate){
            // reconstruir options: DelOption + CartAdd evita reparent
            // de children entre padres distintos.
            for (let _oldopt of [...oldticketproduct.options]){
                oldticketproduct.DelOption(_oldopt);
            }
            for (let _newopt of newticketproduct.options){
                if (_newopt.option){
                    oldticketproduct.CartAdd(_newopt.option);
                }
            }
            // price/charge vienen ya calculados desde ui-view.OnAccept
            // (respetamos reprice manual si aplica).
            oldticketproduct.comments = newticketproduct.comments;
            oldticketproduct.fixed = newticketproduct.fixed;
            oldticketproduct.market = newticketproduct.market;
            oldticketproduct.weight = newticketproduct.weight;
            oldticketproduct.price = newticketproduct.price;
            oldticketproduct.charge = newticketproduct.charge;

            // del+add en lugar de set: ui-change.AddChange no procesa
            // 'set' (reservado para DoCancel). Neto sobre toInsert:
            // sale y entra → _modified=true refresca la vista.
            this.DoChange("del", oldticketproduct);
            this.DoChange("add", oldticketproduct);
        }
        else {
            if (oldticketproduct){
                oldticketproduct.status = 'DE';
                this.DoChange("del", oldticketproduct);
            }

            if (newticketproduct){
                this.AddProduct(newticketproduct);
                this.DoChange("add", newticketproduct);
            }
        }

        this.DoUpdate(true);
    }

    /** Modifica UN producto EXISTENTE in-place (opciones/comentario/reprice).
     *  Notifica a cocina como MODIFICACION solo si la comanda ya se imprimio
     *  y el producto ya estaba persistido; si no, refresca NUEVOS (del+add). */
    CartModify(oldticketproduct: TicketProduct, options: Array <ProductOption>, info = null, comments: string = null, deferUpdate: boolean = false){
        if (!oldticketproduct){
            return;
        }

        this._StampWaiter(oldticketproduct);        // "último que edita" (modificar producto)

        // reconstruir opciones in-place: DelOption + CartAdd crea TicketOption
        // nuevos (no reparenta), asi que reusar el mismo array de options en
        // los N hermanos es seguro — mismo patron que el fast path.
        for (let _oldopt of [...oldticketproduct.options]){
            oldticketproduct.DelOption(_oldopt);
        }
        for (let _option of (options || [])){
            if (_option){
                oldticketproduct.CartAdd(_option);
            }
        }

        if (info){
            oldticketproduct.market = ('market' in info) ? info['market'] : oldticketproduct.market;
            oldticketproduct.weight = ('weight' in info) ? info['weight'] : oldticketproduct.weight;
        }
        oldticketproduct.comments = comments;

        // reprice manual (fixed) si aplica; si no, precio calculado.
        oldticketproduct.fixed = !!(info && info['fixed']);
        oldticketproduct.price = oldticketproduct.TotalPrice;
        oldticketproduct.charge = oldticketproduct.fixed ? (info['charge'] || 0) : oldticketproduct.ChargePrice;

        // MODIFICADOS en cocina solo si la comanda YA se imprimio al menos
        // una vez Y el producto ya estaba persistido en ese envio. Los hijos
        // de un Copy() tienen ToInsert=true (objid null en ctor) aunque sean
        // copia de filas con objid — usar CopyOf para detectarlo.
        let _persisted = !oldticketproduct.ToInsert
                      || !!(oldticketproduct.CopyOf && (oldticketproduct.CopyOf as TicketProduct).objid);
        let _kitchenKnowsProduct = this.printed && _persisted;

        if (_kitchenKnowsProduct){
            this.CartMod(oldticketproduct);
        }
        else {
            this.DoChange("del", oldticketproduct);
            this.DoChange("add", oldticketproduct);
        }

        // deferUpdate=true (OnUpdateAll): NO refrescar por cada hermano. Con
        // refresh por-iteracion el carrito se reagrupa a medias (1 con opcion
        // + resto sin) y la reconciliacion _updateProducts deja grupos
        // fantasma (N reales se PINTAN como N+(N-1), AC_reales confirmo N).
        // Con un unico DoUpdate al final, los N ya tienen la misma opcion y
        // se reagrupan en uno solo. Verificado en dispositivo.
        if (!deferUpdate){
            this.DoUpdate(true);
        }
    }

    CartClear(){
        for(let _ticketproduct of this.products){
            this.CartDel(_ticketproduct);
        }

        for (let _ticketdiscount of this.discounts){
            _ticketdiscount.status = 'DE';
        }

        this.DoUpdate(true);
    }

    CartSort(cartitem, delta, cartitems){
        let _cartproduct = cartitem.product;
        if (!_cartproduct){
            return;     // we can only sort product cart items
        }

        this.TicketControl.ItemSort(_cartproduct, delta, cartitems);    
        for (let _ticketproduct of this.products){
            this.CartUdt(_ticketproduct);
        }
    }

    DiscountAdd(ticketdiscount: TicketDiscount){
        this.AddDiscount(ticketdiscount);

        // notify the ticket change
        if (ticketdiscount){
            /* nothing to do: discounts are not shown in the order */
        }

        this.DoUpdate();
    }

    DiscountDel(ticketdiscount: TicketDiscount, todelete: boolean = true){
        if (todelete){
            ticketdiscount.status = 'DE';
        }

        // notify the ticket change
        if (ticketdiscount){
            /* nothing to do: discounts are not shown in the order */
        }

        this.DoUpdate();
    }
}
